import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import {
  Table, Card, Button, Input, Select, Space, Tag, Modal, Form,
  Upload, message, Row, Col, Tooltip, Popconfirm, Descriptions,
  Tabs, Typography, InputNumber, DatePicker, Divider, Badge,
} from 'antd';
import {
  SearchOutlined, UploadOutlined, DownloadOutlined, PlusOutlined,
  EditOutlined, DeleteOutlined, EyeOutlined, CalendarOutlined,
  BarChartOutlined, ReloadOutlined, MinusCircleOutlined,
  EnvironmentOutlined, FilterOutlined, CloseCircleOutlined,
  FileExcelOutlined, CheckCircleOutlined,
  HistoryOutlined, ExperimentOutlined, AuditOutlined,
  FileDoneOutlined, UserOutlined, TeamOutlined, CarOutlined,
  QrcodeOutlined, PrinterOutlined, QuestionCircleOutlined, MessageOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import dayjs from 'dayjs';
import { RequestTypeBadge, REQUEST_TYPE_META } from '../components/RequestTypeBadge';
import { scheduleAPI, businessTripAPI, masterAPI, qrAPI, inquiryAPI } from '../services/api';
import { getUsername } from '../services/authToken';
import { brand, gray, state, modalWidth, font } from '../theme/tokens';
import { SummerIcon, WinterIcon, SeasonBadge } from '../components/SeasonIcon';
import { EmptyState } from '../components/common';
import PartnerViewModal from '../components/PartnerViewModal';
import InquiryPanel from '../components/InquiryPanel';
import { useFitTableHeight } from '../hooks/useViewportFit';
import { escapeHtml } from '../utils/html';

const DATE_FORMAT = 'YYYY-MM-DD';
/** 문자열 → dayjs (파싱 가능하면 변환, 아니면 null) */
const toDay = (v: string | null | undefined) => {
  if (!v) return null;
  const d = dayjs(v, DATE_FORMAT, true);
  return d.isValid() ? d : null;
};
/** dayjs → 문자열 */
const fromDay = (d: any) => (d && dayjs.isDayjs(d) ? d.format(DATE_FORMAT) : (d || ''));
/** ISO면 dayjs, 텍스트 표기('파종전' 등)면 원문 문자열 유지 — 수정 시 소실 방지 */
const toDayOrText = (v: string | null | undefined) => toDay(v) ?? ((v || '').trim() ? v : null);
/** 작기 레거시 표기(S/E/W) → '하'/'동' 정규화. 매핑 불가 시 undefined */
const normalizeSW = (v: string | null | undefined): string | undefined => {
  const t = (v || '').trim();
  if (['W', 'w', '동'].includes(t)) return '동';
  if (['S', 'E', 's', 'e', '하'].includes(t)) return '하';
  return undefined;
};

const { Option } = Select;
const { RangePicker } = DatePicker;

/** 처리/조사일자 입력 — 날짜값은 DatePicker, 엑셀 유래 텍스트 표기('파종전' 등)는 Input으로 표시·보존 */
const DateOrTextPicker: React.FC<{ value?: any; onChange?: (v: any) => void; width?: number | string }> = ({ value, onChange, width = 148 }) => {
  if (typeof value === 'string' && value.trim()) {
    return <Input value={value} onChange={e => onChange?.(e.target.value)} style={{ width }} />;
  }
  return (
    <DatePicker format={DATE_FORMAT} style={{ width }} placeholder="날짜 선택"
      value={dayjs.isDayjs(value) ? value : null} onChange={onChange} />
  );
};

const TRIP_WORK_TYPES = [
  { value: '외근', label: '외근', icon: '🚗', group: 'out' },
  { value: '오전외근', label: '오전외근', icon: '🚗', group: 'out' },
  { value: '오후외근', label: '오후외근', icon: '🚗', group: 'out' },
  { value: '내근', label: '내근', icon: '🏢', group: 'in' },
  { value: '오전내근', label: '오전내근', icon: '🏢', group: 'in' },
  { value: '오후내근', label: '오후내근', icon: '🏢', group: 'in' },
  { value: '연차', label: '연차', icon: '😴', group: 'leave' },
  { value: '오전반차', label: '오전반차', icon: '😴', group: 'leave' },
  { value: '오후반차', label: '오후반차', icon: '😴', group: 'leave' },
];
const TRIP_TYPE_COLOR: Record<string, { bg: string; border: string; text: string }> = {
  out:   { bg: '#eef2ff', border: '#6366f1', text: '#4f46e5' },
  in:    { bg: '#f0fdf4', border: '#22c55e', text: '#16a34a' },
  leave: { bg: '#fdf4ff', border: '#a855f7', text: '#9333ea' },
};
function isTripOutdoor(wt: string | undefined) {
  return !wt || (TRIP_WORK_TYPES.find(t => t.value === wt)?.group ?? 'out') === 'out';
}

interface TreatmentItem { id?: number; schedule_id?: number; round_no: number; treatment_date: string; note: string; }
interface SurveyItem { id?: number; schedule_id?: number; round_no: number; survey_type?: string; survey_date: string; note: string; }
interface ScheduleCompanyLink { id: number; name: string; code?: string; is_primary?: boolean; }
interface Schedule {
  id: number; seq_no: number | null; test_number: string; title: string;
  test_year: string; company_id: number | null; category_id: number | null;
  location_id: number | null; field_owner_id: number | null;
  company_name: string; company_code: string;
  companies: ScheduleCompanyLink[]; request_type: string;
  category_name: string; location_name: string;
  field_owner_name: string; field_owner_phone: string;
  start_date: string; field_address: string; planting_date: string;
  summer_winter: string; registration_type: string;
  test_start_date: string; test_end_date: string;
  report_date: string; cleanup: string; tims_status: string;
  review_record: string; qrcode: string; status: string;
  has_result_photo: boolean | null;
  partner_confirmed_at: string | null; partner_confirmed_by: string | null;
  /* 목록 응답은 집계만 온다(성능). 배열은 상세 조회(scheduleAPI.get) 후에만 채워진다 */
  treatments?: TreatmentItem[]; surveys?: SurveyItem[];
  treatment_total?: number; treatment_done?: number;
  yakho_total?: number; yakho_done?: number;
  yakhe_total?: number; yakhe_done?: number;
  created_at: string; updated_at: string;
}
interface FilterOption { id: number; name: string; code?: string; phone?: string; company_type?: string; }
interface Filters {
  years: string[]; companies: FilterOption[]; categories: FilterOption[];
  locations: FilterOption[]; owners: FilterOption[];
}
interface Stats {
  total: number; by_year: { name: string; count: number }[];
  by_category: { name: string; count: number }[];
  by_company: { name: string; count: number }[];
  by_location: { name: string; count: number }[];
  by_status: { name: string; count: number }[];
  by_request_type?: { name: string; count: number }[];
}

/* 진행상태 — STATUS_ALL은 표시·조회용(기존 데이터 보존), RETIRED는 신규 선택 불가.
   '성적서작성중'은 2026-08 사용중지: 이미 이 상태인 시험이 남아 있어 색상·필터는 유지하고 폼 선택만 막는다. */
const STATUS_ALL = ['시작전', '처리완료', '조사완료', '성적서작성중', '성적서완료', '발급완료', '최종종료'] as const;
const RETIRED_STATUSES = new Set<string>(['성적서작성중']);
const STATUS_LIST = STATUS_ALL;
const statusColors: Record<string, string> = {
  '처리완료': 'processing',
  '조사완료': 'warning',
  '성적서작성중': 'processing',
  '성적서완료': 'success',
  '발급완료': 'success',
  '최종종료': 'default',
};
const statusTagColors: Record<string, string> = {
  '시작전':    '#94a3b8',
  '처리완료': '#3b82f6',
  '조사완료': '#f59e0b',
  '성적서작성중': '#8b5cf6',
  '성적서완료': '#10b981',
  '발급완료': '#0ea5e9',
  '최종종료': '#64748b',
};
const categoryColors: Record<string, string> = {
  '품목등록-위탁': '#6366f1', '품목등록-자체': '#0ea5e9', '기초시험': '#10b981', '지역적응성시험': '#f59e0b',
  '살균제': '#f472b6', '살균': '#f472b6',   // 분홍
  '살충제': '#22c55e', '살충': '#22c55e',   // 초록
  '제초제': '#eab308', '제초': '#eab308',   // 노랑
};
const getCategoryColor = (name: string) => categoryColors[name] || '#6366f1';

/* 통계분석 구분별 차트 — 대시보드 '구분별 시험현황' 도넛과 동일 표기('~제')·색상 */
const CATEGORY_DISPLAY_NAME: Record<string, string> = { '살균': '살균제', '살충': '살충제', '제초': '제초제' };
const categoryDisplayName = (name: string) => CATEGORY_DISPLAY_NAME[name] || name;
const DASH_CATEGORY_COLORS: Record<string, string> = {
  '살균':   '#f472b6', '살균제': '#f472b6',
  '살충':   '#10b981', '살충제': '#10b981',
  '제초제': '#facc15', '제초':   '#facc15',
  '기초시험': '#f59e0b',
};
const DASH_FALLBACK_COLORS = ['#6366f1', '#0ea5e9', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#f97316', '#14b8a6', '#e879f9', '#06b6d4'];
/* 대시보드 도넛과 동일한 표시 순서 — 살균제 → 살충제 → 제초제 → 기초시험 → 기타 */
const CATEGORY_LEGEND_ORDER = ['살균제', '살균', '살충제', '살충', '제초제', '제초', '기초시험'];
const categoryRank = (name: string) => {
  const i = CATEGORY_LEGEND_ORDER.indexOf(name);
  return i < 0 ? CATEGORY_LEGEND_ORDER.length : i;
};

/* 의뢰구분 배지는 components/RequestTypeBadge 로 분리 — 기존 import 경로 호환을 위해 재수출한다 */
export { RequestTypeBadge, REQUEST_TYPE_META } from '../components/RequestTypeBadge';

const SchedulePage: React.FC = () => {
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<Filters>({ years: [], companies: [], categories: [], locations: [], owners: [] });
  const [stats, setStats] = useState<Stats | null>(null);
  const [searchText, setSearchText] = useState('');
  const [search, setSearch] = useState('');
  // 시험년도 — N개 다중 선택 (서버에는 콤마로 이어 보낸다). 기본값은 올해 1개.
  // DB의 test_year 는 2자리 저장('26')이라 옵션 값과 맞춰 2자리로 둔다 (백엔드는 4자리도 허용)
  const [filterYears, setFilterYears] = useState<string[]>([String(new Date().getFullYear()).slice(-2)]);
  const filterYearParam = useMemo(() => filterYears.join(','), [filterYears]);
  /* 서버 페이징 (2026-08). 301건을 통째로 받아 클라이언트에서 거르던 구조를 옮겼다 —
     작기 판정·상태 집계까지 서버로 넘겨야 페이지 단위 조회가 성립한다. */
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [sortBy, setSortBy] = useState('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});
  const [scopedTotal, setScopedTotal] = useState(0);

  // 작기(하/동작물) 퀵필터 — N개 다중 선택. 비어 있으면 '전체'(연도 필터 기준 조회)
  const [filterPeriods, setFilterPeriods] = useState<string[]>([]);
  const hasPeriod = filterPeriods.length > 0;
  const [filterCategory, setFilterCategory] = useState<number>(0);
  const [filterCompany, setFilterCompany] = useState<number>(0);
  const [filterRequestType, setFilterRequestType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [activeTab, setActiveTab] = useState('table');

  /* 표 본문 높이 실측 — 페이지 세로스크롤 없이 페이지네이션까지 뷰포트 안에 들어오게 한다 */
  const { tableWrapRef, cardWrapRef, bodyHeight } = useFitTableHeight();

  const [detailVisible, setDetailVisible] = useState(false);
  const [detailRecord, setDetailRecord] = useState<Schedule | null>(null);
  // 파트너 문의/요청 — 목록 배지 + 시험별 패널
  const [inquiryStats, setInquiryStats] = useState<Record<number, { total: number; waiting: number; unread: number }>>({});
  const [inquiryRecord, setInquiryRecord] = useState<Schedule | null>(null);
  // 선택 n건 일자 일괄등록 (성적서발급일 / TIMS입력현황)
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [bulkDateOpen, setBulkDateOpen] = useState(false);
  const [bulkDateSaving, setBulkDateSaving] = useState(false);
  const [bulkDateForm] = Form.useForm();

  const loadInquiryStats = useCallback(() => {
    inquiryAPI.scheduleSummary()
      .then(({ data }) => {
        const map: Record<number, { total: number; waiting: number; unread: number }> = {};
        (data || []).forEach((r: any) => {
          map[r.schedule_id] = { total: r.total, waiting: r.waiting, unread: r.unread };
        });
        setInquiryStats(map);
      })
      .catch(() => setInquiryStats({}));
  }, []);

  useEffect(() => { loadInquiryStats(); }, [loadInquiryStats]);
  const [formVisible, setFormVisible] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form] = Form.useForm();
  const [treatmentModalVisible, setTreatmentModalVisible] = useState(false);
  const [surveyModalVisible, setSurveyModalVisible] = useState(false);
  const [surveyModalType, setSurveyModalType] = useState<'약효' | '약해'>('약효');
  const [itemModalSchedule, setItemModalSchedule] = useState<Schedule | null>(null);
  const [treatmentForm] = Form.useForm();
  const [surveyForm] = Form.useForm();
  const [itemModalSaving, setItemModalSaving] = useState(false);

  // 출장 등록 모달
  const [tripModalVisible, setTripModalVisible] = useState(false);
  const [tripForm] = Form.useForm();
  const [tripSaving, setTripSaving] = useState(false);
  const [tripResearchers, setTripResearchers] = useState<string[]>([]);
  const [tripVehicles, setTripVehicles] = useState<string[]>([]);
  const tripWatchedWorkType = Form.useWatch('work_type', tripForm);

  /* 행 상세(처리·조사 배열) 캐시 — 목록 갱신 때마다 비운다 */
  const detailCache = useRef<Map<number, Schedule>>(new Map());

  /* 상태를 제외한 공통 조회 조건 — 목록과 상태집계가 같은 기준을 써야 카드와 표가 어긋나지 않는다 */
  const scopeParams = useMemo(() => ({
    search,
    test_year: hasPeriod ? undefined : (filterYearParam || undefined),
    category_id: filterCategory || undefined,
    company_id: filterCompany || undefined,
    request_type: filterRequestType || undefined,
    period: hasPeriod ? filterPeriods.join(',') : undefined,
  }), [search, filterYearParam, hasPeriod, filterPeriods, filterCategory, filterCompany, filterRequestType]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      /* 진행상태는 목록에만 걸고 상태집계에는 넣지 않는다.
         카드가 '검색 결과의 상태별 분포'를 보여줘야 하는데 집계까지 걸러 오면
         상태 하나를 고르는 순간 나머지 카드가 전부 0이 되어 분포를 못 읽는다. */
      const [listRes, sumRes] = await Promise.all([
        scheduleAPI.list({ ...scopeParams, status: filterStatus || undefined,
                           page, page_size: pageSize,
                           sort_by: sortBy || undefined, sort_dir: sortDir }),
        scheduleAPI.statusSummary(scopeParams),
      ]);
      detailCache.current.clear();   // 목록이 갱신되면 행 상세 캐시도 무효 (다른 사용자의 수정 반영)
      const body = listRes.data;
      setSchedules(body.items || []);
      setTotal(body.total || 0);
      setStatusCounts(sumRes.data?.counts || {});
      setScopedTotal(sumRes.data?.total || 0);
    } catch (err: any) { message.error(err.response?.data?.detail || '데이터를 불러오는데 실패했습니다.'); }
    finally { setLoading(false); }
  }, [scopeParams, filterStatus, page, pageSize, sortBy, sortDir]);

  /* 필터 드롭다운 옵션은 검색 조건과 무관하다 — 필터를 바꿀 때마다 다시 받을 이유가 없어
     최초 1회 + 데이터가 바뀐 뒤에만 조회한다 */
  const fetchMeta = useCallback(async () => {
    try {
      const { data } = await scheduleAPI.filters();
      setFilters(data);
    } catch { /* 옵션 조회 실패는 목록 조회를 막지 않는다 */ }
  }, []);
  useEffect(() => { fetchMeta(); }, [fetchMeta]);

  /* 통계는 '통계 분석' 탭 차트에서만 쓴다 — 탭을 열 때 한 번만 받는다(목록만 보는 사용자는 아예 안 부른다) */
  const fetchStats = useCallback(async () => {
    try {
      const { data } = await scheduleAPI.stats();
      setStats(data);
    } catch { /* 차트 없이도 목록은 동작한다 */ }
  }, []);
  useEffect(() => { if (activeTab === 'stats' && !stats) fetchStats(); }, [activeTab, stats, fetchStats]);

  /* 등록·수정·삭제·엑셀 업로드처럼 데이터가 바뀐 뒤에는 목록뿐 아니라
     필터 옵션(새 의뢰사·연도)과 이미 받아둔 통계도 함께 갱신한다 */
  const reload = useCallback(() => {
    fetchData();
    fetchMeta();
    if (stats) fetchStats();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchData, fetchMeta, fetchStats, stats]);


  /* 조건이 바뀌면 1페이지로 — 3페이지를 보다 필터를 바꾸면 빈 화면이 나온다 */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setPage(1); }, [scopeParams, filterStatus, pageSize]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchText);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchText]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // 출장 마스터 데이터 로드
  useEffect(() => {
    masterAPI.researchers.list().then(r => setTripResearchers(r.data.map((x: any) => x.name))).catch(() => {});
    masterAPI.vehicles.list().then(r => setTripVehicles(r.data.map((x: any) => x.vehicle_number))).catch(() => {});
  }, []);

  const openTripModal = (record: Schedule) => {
    tripForm.resetFields();
    const loginUser = getUsername() || '';
    tripForm.setFieldsValue({
      work_type: '외근',
      test_number: record.test_number,
      date_range: [dayjs(), dayjs()],
      researchers: loginUser ? [loginUser] : [],
    });
    setTripModalVisible(true);
  };

  const handleTripSave = async () => {
    try {
      const vals = await tripForm.validateFields();
      setTripSaving(true);
      const dr = vals.date_range;
      const start = dr?.[0] || null;
      const end = dr?.[1] || null;
      const researchers = Array.isArray(vals.researchers)
        ? vals.researchers.join(', ') : (vals.researchers || '');
      const vehicles = Array.isArray(vals.vehicle_number)
        ? vals.vehicle_number.join(', ') : (vals.vehicle_number || null);
      await businessTripAPI.create({
        ...vals,
        researchers,
        vehicle_number: vehicles,
        trip_start_date: start ? start.format('YYYY-MM-DDTHH:mm:ss') : null,
        trip_end_date: end ? end.format('YYYY-MM-DDTHH:mm:ss') : (start ? start.format('YYYY-MM-DDTHH:mm:ss') : null),
        trip_date_raw: start ? start.format('YYYY-MM-DD') : null,
        supervisor_confirmed_at: vals.supervisor_confirmed_at
          ? vals.supervisor_confirmed_at.format('YYYY-MM-DDTHH:mm:ss') : null,
      });
      message.success('출장이 등록되었습니다.');
      setTripModalVisible(false);
    } catch (e: any) {
      if (e?.response?.data?.detail) message.error(e.response.data.detail);
    } finally {
      setTripSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    await scheduleAPI.delete(id); message.success('삭제되었습니다.'); reload();
  };

  const handleUpload = async (file: File) => {
    try {
      const { data } = await scheduleAPI.uploadExcel(file);
      const mc = data.master_created;
      message.success(`${data.message} | 마스터 자동생성: 의뢰사 ${mc.companies}, 장소 ${mc.locations}, 구분 ${mc.categories}, 포장주 ${mc.owners}`);
      reload();
    } catch (err: any) { message.error(err.response?.data?.detail || '업로드 실패'); }
    return false;
  };

  const handleExport = async () => {
    try {
      const res = await scheduleAPI.exportExcel(filterYearParam);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = `시험일정총괄표_${filterYears.join('-') || '전체'}.xlsx`;
      a.click(); window.URL.revokeObjectURL(url);
    } catch (err: any) { message.error(err.response?.data?.detail || 'Excel 다운로드에 실패했습니다.'); }
  };

  const handleTemplateDownload = async () => {
    try {
      const res = await scheduleAPI.downloadTemplate();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = '시험일정총괄표_양식.xlsx';
      a.click(); window.URL.revokeObjectURL(url);
    } catch (err: any) { message.error(err.response?.data?.detail || '양식 다운로드에 실패했습니다.'); }
  };

  const handleFormSubmit = async () => {
    try {
      const values = await form.validateFields();
      // dayjs → 문자열 변환 (start_date는 텍스트 입력)
      values.planting_date = fromDay(values.planting_date);
      values.report_date = fromDay(values.report_date);
      values.tims_status = fromDay(values.tims_status);
      // 작기: 미선택(undefined)은 ''로 전송해 의도적 해제로 처리 (undefined는 axios가 생략 → 기존값 보존됨)
      values.summer_winter = values.summer_winter || '';
      // 처리/조사 배열 정리 + dayjs 변환
      values.treatments = (values.treatments || [])
        .map((t: any) => ({ ...t, treatment_date: fromDay(t?.treatment_date) }))
        .filter((t: any) => t && t.treatment_date);
      const yakho = (values.surveys_yakho || [])
        .map((s: any) => ({ ...s, survey_date: fromDay(s?.survey_date), survey_type: '약효' }))
        .filter((s: any) => s && s.survey_date);
      const yakhe = (values.surveys_yakhe || [])
        .map((s: any) => ({ ...s, survey_date: fromDay(s?.survey_date), survey_type: '약해' }))
        .filter((s: any) => s && s.survey_date);
      values.surveys = [...yakho, ...yakhe];
      // 날짜 순서 검증 — 개시일이 ISO(YYYY-MM-DD) 형식일 때만 비교
      // ('7월 상순' 등 텍스트 표기는 문자열 비교 시 오탐이므로 검증 제외)
      if (values.start_date && values.report_date) {
        const startStr = String(values.start_date).trim();
        const reportStr = typeof values.report_date === 'string' ? values.report_date : values.report_date.format('YYYY-MM-DD');
        if (/^\d{4}-\d{2}-\d{2}$/.test(startStr) && startStr > reportStr) {
          message.warning('개시일이 성적서발급일보다 늦습니다. 날짜를 확인해주세요.');
        }
      }
      if (editingId) {
        await scheduleAPI.update(editingId, values); message.success('수정되었습니다.');
      } else {
        await scheduleAPI.create(values); message.success('등록되었습니다.');
      }
      setFormVisible(false); form.resetFields(); setEditingId(null); reload();
    } catch (err: any) {
      if (err.response?.data?.detail) message.error(err.response.data.detail);
    }
  };

  const [timelineVisible, setTimelineVisible] = useState(false);
  const [timelineRecord, setTimelineRecord] = useState<Schedule | null>(null);
  const [statusChanging, setStatusChanging] = useState(false);

  // 파트너 화면 미리보기 모달 (partner.sappt.co.kr 상세와 동일 레이아웃)
  const [partnerViewVisible, setPartnerViewVisible] = useState(false);
  const [partnerViewRecord, setPartnerViewRecord] = useState<Schedule | null>(null);

  const handleQuickStatus = async (newStatus: string) => {
    if (!editingId) return;
    setStatusChanging(true);
    try {
      const values = await form.validateFields();
      // start_date는 텍스트 입력 — dayjs 변환 불필요
      values.planting_date = fromDay(values.planting_date);
      values.report_date   = fromDay(values.report_date);
      values.tims_status   = fromDay(values.tims_status);
      values.treatments = (values.treatments || [])
        .map((t: any) => ({ ...t, treatment_date: fromDay(t?.treatment_date) }))
        .filter((t: any) => t && t.treatment_date);
      // 조사일정은 폼 필드(surveys_yakho/yakhe)에서 병합 — 존재하지 않는 values.surveys 참조 시 전체 삭제됨
      const qsYakho = (values.surveys_yakho || [])
        .map((s: any) => ({ ...s, survey_date: fromDay(s?.survey_date), survey_type: '약효' }))
        .filter((s: any) => s && s.survey_date);
      const qsYakhe = (values.surveys_yakhe || [])
        .map((s: any) => ({ ...s, survey_date: fromDay(s?.survey_date), survey_type: '약해' }))
        .filter((s: any) => s && s.survey_date);
      values.surveys = [...qsYakho, ...qsYakhe];
      values.summer_winter = values.summer_winter || '';
      values.status = newStatus;
      await scheduleAPI.update(editingId, values);
      form.setFieldValue('status', newStatus);
      message.success(
        <span>상태가 <strong style={{ color: statusTagColors[newStatus] }}>{newStatus}</strong>(으)로 변경되었습니다.</span>
      );
      reload();
    } catch (err: any) {
      if (err.response?.data?.detail) message.error(err.response.data.detail);
    } finally { setStatusChanging(false); }
  };

  const openEdit = async (rec: Schedule) => {
    const record = await loadDetail(rec);
    setEditingId(record.id);
    const vals: any = { ...record };
    // 문자열 → dayjs 변환 (start_date는 텍스트 입력 — 변환 불필요)
    vals.planting_date = toDay(vals.planting_date);
    vals.report_date = toDay(vals.report_date);
    vals.tims_status = toDay(vals.tims_status);
    // 작기: 레거시 S/E/W 표기를 폼 표준값('하'/'동')으로 변환해 표시
    vals.summer_winter = normalizeSW(vals.summer_winter);
    // 의뢰사 다중선택: companies(대표 우선 정렬) → id 배열, 링크 없으면 company_id 단수 폴백
    vals.company_ids = (record.companies || []).map(c => c.id);
    if (!vals.company_ids.length && record.company_id) vals.company_ids = [record.company_id];
    vals.request_type = record.request_type || '비협회';
    // treatments/surveys 일자 변환
    vals.treatments = (vals.treatments || []).map((t: any) => ({ ...t, treatment_date: toDayOrText(t.treatment_date) }));
    const allSurveys = (vals.surveys || []).map((s: any) => ({ ...s, survey_date: toDayOrText(s.survey_date) }));
    vals.surveys_yakho = allSurveys.filter((s: any) => !s.survey_type || s.survey_type === '약효');
    vals.surveys_yakhe = allSurveys.filter((s: any) => s.survey_type === '약해');
    if (!vals.treatments?.length) vals.treatments = [{ round_no: 1, treatment_date: null, note: '' }];
    if (!vals.surveys_yakho?.length) vals.surveys_yakho = [{ round_no: 1, survey_date: null, note: '' }];
    if (!vals.surveys_yakhe?.length) vals.surveys_yakhe = [{ round_no: 1, survey_date: null, note: '' }];
    form.setFieldsValue(vals);
    setFormVisible(true);
  };

  const openNew = () => {
    setEditingId(null);
    form.resetFields();
    form.setFieldsValue({
      treatments: [{ round_no: 1, treatment_date: null, note: '' }],
      surveys_yakho: [{ round_no: 1, survey_date: null, note: '' }],
      surveys_yakhe: [{ round_no: 1, survey_date: null, note: '' }],
      status: '시작전',
      request_type: '비협회',
    });
    setFormVisible(true);
  };

  const buildSchedulePayload = (record: Schedule, overrides: object) => ({
    seq_no: record.seq_no,
    test_number: record.test_number,
    title: record.title,
    test_year: record.test_year,
    company_id: record.company_id,
    company_ids: (record.companies || []).length
      ? (record.companies || []).map(c => c.id)
      : (record.company_id ? [record.company_id] : []),
    request_type: record.request_type || '비협회',
    category_id: record.category_id,
    location_id: record.location_id,
    field_owner_id: record.field_owner_id,
    status: record.status,
    start_date: record.start_date,
    field_address: record.field_address,
    planting_date: record.planting_date,
    report_date: record.report_date,
    cleanup: record.cleanup,
    tims_status: record.tims_status,
    review_record: record.review_record,
    qrcode: record.qrcode,
    summer_winter: record.summer_winter,
    treatments: record.treatments,
    surveys: record.surveys,
    ...overrides,
  });

  /* 목록은 처리/조사 배열을 받지 않는다(행당 83%가 표에 안 쓰이는 데이터였다).
     상세·수정·회차 모달을 열 때만 GET /api/schedules/{id} 로 받아 채운다. 같은 행은 캐시. */
  const loadDetail = useCallback(async (record: Schedule): Promise<Schedule> => {
    if (record.treatments && record.surveys) return record;
    const cached = detailCache.current.get(record.id);
    if (cached) return { ...record, ...cached };
    try {
      const { data } = await scheduleAPI.get(record.id);
      detailCache.current.set(record.id, data);
      return { ...record, ...data };
    } catch {
      message.error('상세 정보를 불러오지 못했습니다.');
      return { ...record, treatments: [], surveys: [] };
    }
  }, []);

  const openTreatmentModal = async (rec: Schedule) => {
    const record = await loadDetail(rec);
    setItemModalSchedule(record);
    treatmentForm.setFieldsValue({
      treatments: (record.treatments || []).length
        ? (record.treatments || []).map(t => ({ ...t, treatment_date: toDayOrText(t.treatment_date) }))
        : [{ round_no: 1, treatment_date: null, note: '' }],
    });
    setTreatmentModalVisible(true);
  };

  const openSurveyModal = async (rec: Schedule, type: '약효' | '약해') => {
    const record = await loadDetail(rec);
    setItemModalSchedule(record);
    setSurveyModalType(type);
    const all = (record.surveys || []).map(s => ({ ...s, survey_date: toDayOrText(s.survey_date) }));
    const yakho = all.filter(s => !s.survey_type || s.survey_type === '약효');
    const yakhe = all.filter(s => s.survey_type === '약해');
    surveyForm.setFieldsValue({
      surveys_yakho: yakho.length ? yakho : [{ round_no: 1, survey_date: null, note: '' }],
      surveys_yakhe: yakhe.length ? yakhe : [{ round_no: 1, survey_date: null, note: '' }],
    });
    setSurveyModalVisible(true);
  };

  const handleTreatmentSave = async () => {
    if (!itemModalSchedule) return;
    setItemModalSaving(true);
    try {
      const values = await treatmentForm.validateFields();
      const treatments = (values.treatments || [])
        .map((t: any) => ({ ...t, treatment_date: fromDay(t.treatment_date) }))
        .filter((t: any) => t.treatment_date);
      await scheduleAPI.update(itemModalSchedule.id, buildSchedulePayload(itemModalSchedule, { treatments }));
      message.success('처리일정이 저장되었습니다.');
      setTreatmentModalVisible(false);
      reload();
    } catch (err: any) {
      if (err.response?.data?.detail) message.error(err.response.data.detail);
    } finally { setItemModalSaving(false); }
  };

  const handleSurveySave = async () => {
    if (!itemModalSchedule) return;
    setItemModalSaving(true);
    try {
      const values = await surveyForm.validateFields();
      // 현재 편집 중인 type만 저장, 나머지 type은 기존 데이터 보존
      const existingAll = itemModalSchedule.surveys || [];
      let surveys: any[] = [];
      if (surveyModalType === '약효') {
        const preserved = existingAll
          .filter(s => s.survey_type === '약해')
          .map(s => ({ ...s, survey_type: '약해' }));
        const updated = (values.surveys_yakho || [])
          .map((s: any) => ({ ...s, survey_date: fromDay(s.survey_date), survey_type: '약효' }))
          .filter((s: any) => s.survey_date);
        surveys = [...updated, ...preserved];
      } else {
        const preserved = existingAll
          .filter(s => !s.survey_type || s.survey_type === '약효')
          .map(s => ({ ...s, survey_type: '약효' }));
        const updated = (values.surveys_yakhe || [])
          .map((s: any) => ({ ...s, survey_date: fromDay(s.survey_date), survey_type: '약해' }))
          .filter((s: any) => s.survey_date);
        surveys = [...preserved, ...updated];
      }
      await scheduleAPI.update(itemModalSchedule.id, buildSchedulePayload(itemModalSchedule, { surveys }));
      message.success(`${surveyModalType === '약효' ? '약효' : '약해'}조사일정이 저장되었습니다.`);
      setSurveyModalVisible(false);
      reload();
    } catch (err: any) {
      if (err.response?.data?.detail) message.error(err.response.data.detail);
    } finally { setItemModalSaving(false); }
  };

  const columns = useMemo(() => [
    { title: '연번', dataIndex: 'seq_no', width: 55, fixed: 'left' as const,
      sorter: true },
    { title: '시험번호', dataIndex: 'test_number', width: 145, fixed: 'left' as const,
      sorter: true,
      render: (v: string) => <Typography.Text strong style={{ color: brand.primary, fontSize: 12 }}>{v}</Typography.Text> },
    { title: '항목명', dataIndex: 'title', width: 260, ellipsis: true,
      sorter: true,
      render: (v: string, r: Schedule) => <Tooltip title={v}><a onClick={async () => { setDetailRecord(await loadDetail(r)); setDetailVisible(true); }}>{v}</a></Tooltip> },
    { title: '의뢰구분', dataIndex: 'request_type', width: 82, align: 'center' as const,
      sorter: true,
      render: (v: string) => <RequestTypeBadge value={v} /> },
    { title: '의뢰사', dataIndex: 'company_name', width: 110,
      sorter: true,
      render: (v: string, r: Schedule) => {
        const extras = (r.companies || []).filter(c => !c.is_primary);
        if (!v && !extras.length) return '-';
        return (
          <Space size={2}>
            {v && <Tag color="blue" style={{ fontWeight: 600, marginRight: 0 }}>{v}</Tag>}
            {extras.length > 0 && (
              <Tooltip title={`추가 의뢰사: ${extras.map(c => c.name).join(', ')}`}>
                <Tag style={{ fontWeight: 700, color: '#6366f1', borderColor: '#c7d2fe', background: '#eef2ff', marginRight: 0, cursor: 'default' }}>
                  +{extras.length}
                </Tag>
              </Tooltip>
            )}
          </Space>
        );
      } },
    { title: '구분', dataIndex: 'category_name', width: 80,
      sorter: true,
      render: (v: string) => v
        ? <Tag style={{ fontWeight: 600, borderColor: getCategoryColor(v), color: getCategoryColor(v), background: getCategoryColor(v) + '18' }}>{v}</Tag>
        : '-' },
    { title: '작기', dataIndex: 'summer_winter', width: 56, align: 'center' as const,
      render: (v: string) => <SeasonBadge value={v} /> },
    {
      title: '처리',
      key: 'treatment_progress',
      width: 70,
      align: 'center' as const,
      render: (_: any, record: Schedule) => {
        const total = record.treatment_total ?? (record.treatments || []).length;
        const filled = record.treatment_done ?? (record.treatments || []).filter((t: any) => t.treatment_date).length;
        const color = total === 0 ? '#94a3b8' : filled === total ? '#10b981' : '#f59e0b';
        return (
          <Tooltip title="처리일정 보기/편집">
            <a style={{ fontSize: 12, fontWeight: 600, color }} onClick={() => openTreatmentModal(record)}>
              {filled}/{total}
            </a>
          </Tooltip>
        );
      },
    },
    {
      title: '약효조사',
      key: 'survey_yakho',
      width: 72,
      align: 'center' as const,
      render: (_: any, record: Schedule) => {
        const yakho = (record.surveys || []).filter((s: any) => !s.survey_type || s.survey_type === '약효');
        const total = record.yakho_total ?? yakho.length;
        const filled = record.yakho_done ?? yakho.filter((s: any) => s.survey_date).length;
        const color = total === 0 ? '#94a3b8' : filled === total ? '#10b981' : '#f59e0b';
        return (
          <Tooltip title="약효조사일정 등록/수정">
            <a style={{ fontSize: 12, fontWeight: 600, color }}
              onClick={e => { e.stopPropagation(); openSurveyModal(record, '약효'); }}>
              {filled}/{total}
            </a>
          </Tooltip>
        );
      },
    },
    {
      title: '약해조사',
      key: 'survey_yakhe',
      width: 72,
      align: 'center' as const,
      render: (_: any, record: Schedule) => {
        const yakhe = (record.surveys || []).filter((s: any) => s.survey_type === '약해');
        const total = record.yakhe_total ?? yakhe.length;
        const filled = record.yakhe_done ?? yakhe.filter((s: any) => s.survey_date).length;
        if (total === 0) {
          return (
            <Tooltip title="약해조사일정 등록">
              <a style={{ fontSize: 11, color: '#cbd5e1' }}
                onClick={e => { e.stopPropagation(); openSurveyModal(record, '약해'); }}>
                + 등록
              </a>
            </Tooltip>
          );
        }
        const color = filled === total ? '#ea580c' : '#f97316';
        return (
          <Tooltip title="약해조사일정 등록/수정">
            <a style={{ fontSize: 12, fontWeight: 600, color }}
              onClick={e => { e.stopPropagation(); openSurveyModal(record, '약해'); }}>
              {filled}/{total}
            </a>
          </Tooltip>
        );
      },
    },
    { title: '상태', dataIndex: 'status', width: 90, fixed: 'right' as const,
      sorter: true,
      render: (v: string, record: Schedule) => {
        const hasTreatment = (record.treatment_done ?? (record.treatments || []).filter(t => t.treatment_date).length) > 0;
        const label = !hasTreatment ? '시작전' : (v || '-');
        return <Tag color={statusTagColors[label] || '#64748b'} style={{ fontSize: 11, padding: '0 6px' }}>{label}</Tag>;
      } },
    { title: '문의', width: 58, fixed: 'right' as const, align: 'center' as const,
      render: (_: any, record: Schedule) => {
        const st = inquiryStats[record.id];
        const total = st?.total || 0;
        const waiting = st?.waiting || 0;
        if (!total) {
          return <Tooltip title="접수된 파트너 문의 없음"><MessageOutlined style={{ color: '#e2e8f0', fontSize: 15 }} /></Tooltip>;
        }
        // 답변대기 → 빨강 강조, 모두 답변완료 → 인디고 정보 배지
        return (
          <Tooltip title={waiting > 0
            ? `파트너 문의 ${total}건 · 답변대기 ${waiting}건 — 클릭하면 바로 답변할 수 있습니다`
            : `파트너 문의 ${total}건 (모두 답변완료)`}>
            <Badge
              count={waiting > 0 ? waiting : total}
              size="small"
              offset={[-3, 3]}
              styles={{
                indicator: waiting > 0
                  ? { boxShadow: '0 0 0 2px #fff', fontWeight: 700 }
                  : { background: '#e0e7ff', color: brand.primary, border: '1px solid #c7d2fe', boxShadow: 'none', fontWeight: 700 },
              }}
            >
              <Button
                size="small" type="text"
                icon={<MessageOutlined style={{ color: waiting > 0 ? '#ef4444' : brand.primary, fontSize: 15 }} />}
                onClick={(e) => { e.stopPropagation(); setInquiryRecord(record); }}
              />
            </Badge>
          </Tooltip>
        );
      },
    },
    { title: '', width: 168, fixed: 'right' as const,
      render: (_: any, record: Schedule) => (
        <Space size={4}>
          <Tooltip title="진행 타임라인"><Button size="small" type="text" icon={<HistoryOutlined style={{ color: '#8b5cf6' }} />} onClick={() => { setTimelineRecord(record); setTimelineVisible(true); }} /></Tooltip>
          <Tooltip title="상세"><Button size="small" type="text" icon={<EyeOutlined />} onClick={async () => { setDetailRecord(await loadDetail(record)); setDetailVisible(true); }} /></Tooltip>
          <Tooltip title="파트너 화면 보기"><Button size="small" type="text" icon={<TeamOutlined style={{ color: '#f59e0b' }} />} onClick={() => { setPartnerViewRecord(record); setPartnerViewVisible(true); }} /></Tooltip>
          <Tooltip title="업무 및 출장 등록"><Button size="small" type="text" icon={<CarOutlined style={{ color: brand.primary }} />} onClick={() => openTripModal(record)} /></Tooltip>
          <Tooltip title="수정"><Button size="small" type="text" icon={<EditOutlined />} onClick={() => openEdit(record)} /></Tooltip>
          <Popconfirm title="삭제하시겠습니까?" onConfirm={() => handleDelete(record.id)} okText="삭제" cancelText="취소">
            <Tooltip title="삭제"><Button size="small" type="text" danger icon={<DeleteOutlined />} /></Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ], [inquiryStats]);

  const companyChart = useMemo(() => stats ? {
    title: { text: '의뢰사별', left: 'center', textStyle: { fontSize: 14, color: gray[700], fontWeight: 600 } },
    tooltip: { trigger: 'item' as const },
    series: [{ type: 'pie' as const, radius: ['35%','65%'], data: stats.by_company.map(d => ({ name: d.name, value: d.count })),
      itemStyle: { borderRadius: 6, borderColor: '#fff', borderWidth: 2 }, label: { fontSize: 11, color: gray[500] },
      color: ['#6366f1','#0ea5e9','#10b981','#f59e0b','#ef4444','#8b5cf6','#f97316','#14b8a6','#e879f9','#fb923c'] }],
  } : {}, [stats]);
  const categoryChart = useMemo(() => {
    if (!stats) return {};
    const cats = [...stats.by_category].sort((a, b) => categoryRank(a.name) - categoryRank(b.name));
    return {
      title: { text: '구분별', left: 'center', textStyle: { fontSize: 14, color: gray[700], fontWeight: 600 } },
      tooltip: { trigger: 'axis' as const }, grid: { top: 50, bottom: 30, left: 60, right: 20 },
      xAxis: { type: 'category' as const, data: cats.map(d => categoryDisplayName(d.name)), axisLabel: { color: gray[500], fontSize: 11 } },
      yAxis: { type: 'value' as const, minInterval: 1 },
      series: [{ data: cats.map((d, i) => ({ value: d.count,
          itemStyle: { color: DASH_CATEGORY_COLORS[d.name] || DASH_FALLBACK_COLORS[i % DASH_FALLBACK_COLORS.length], borderRadius: [6, 6, 0, 0] } })),
        type: 'bar' as const, barWidth: '50%' }],
    };
  }, [stats]);
  const requestTypeChart = useMemo(() => stats ? {
    title: { text: '의뢰구분별 (협회/비협회)', left: 'center', textStyle: { fontSize: 14, color: gray[700], fontWeight: 600 } },
    tooltip: { trigger: 'item' as const },
    series: [{ type: 'pie' as const, radius: ['40%','68%'],
      data: (stats.by_request_type || []).map(d => ({
        name: d.name, value: d.count,
        itemStyle: { color: REQUEST_TYPE_META[d.name]?.color || '#94a3b8' },
      })),
      itemStyle: { borderRadius: 6, borderColor: '#fff', borderWidth: 2 },
      label: { fontSize: 12, color: gray[600], formatter: '{b}\n{c}건' } }],
  } : {}, [stats]);
  const locationChart = useMemo(() => stats ? {
    title: { text: '시험장소별 (상위 15)', left: 'center', textStyle: { fontSize: 14, color: gray[700], fontWeight: 600 } },
    tooltip: { trigger: 'axis' as const }, grid: { top: 50, bottom: 80, left: 50, right: 20 },
    xAxis: { type: 'category' as const, data: stats.by_location.sort((a,b) => b.count - a.count).slice(0,15).map(d => d.name), axisLabel: { color: gray[500], fontSize: 10, rotate: 45 } },
    yAxis: { type: 'value' as const, minInterval: 1 },
    series: [{ data: stats.by_location.sort((a,b) => b.count - a.count).slice(0,15).map(d => d.count), type: 'bar' as const, barWidth: '60%', itemStyle: { color: '#0ea5e9', borderRadius:[4,4,0,0] } }],
  } : {}, [stats]);

  // '필터 초기화'가 작기도 함께 지우므로 개수에도 포함한다
  const activeFilterCount = [filterYears.length > 0, hasPeriod, filterCategory, filterCompany, filterRequestType, filterStatus].filter(Boolean).length;
  const clearAllFilters = () => { setFilterYears([]); setFilterCategory(0); setFilterCompany(0); setFilterRequestType(''); setFilterStatus(''); setSearchText(''); setFilterPeriods([]); };

  // 퀵필터 기간 정의 (당년/전년 동적 계산)
  const _now = new Date();
  const currYY = String(_now.getFullYear() % 100).padStart(2, '0');        // '26'
  const prevYY = String((_now.getFullYear() - 1) % 100).padStart(2, '0'); // '25'
  const nextYY = String((_now.getFullYear() + 1) % 100).padStart(2, '0'); // '27'
  // 동작물 시즌은 시작연도 기준(시험번호 SRnn=계약·시작연도): SR25I-* = 25~26 동작기, SR26-*W = 26~27 동작기.
  // 단, test_year는 수확연도(시작연도+1)로 등록된 레코드도 혼재 → endYears로 보정 매칭.
  const SUMMER_SW = ['S','E','s','e','하'];
  const WINTER_SW = ['W','동','w'];
  /* label 은 '필터 초기화' 옆 요약 문구가 쓰는 정식 명칭, short 는 필터 줄의 알약용.
     알약에는 ☀️하/🧊동 배지가 이미 붙으므로 '하작물/동작물'을 또 적으면 중복이다. */
  const PERIOD_PRESETS = [
    { key: 'prev_h',  label: `${prevYY}년 하작물`,           short: `${prevYY}년`,           years: [prevYY, `20${prevYY}`], sw: SUMMER_SW },
    { key: 'prev_d',  label: `${prevYY}~${currYY}년 동작물`, short: `${prevYY}~${currYY}년`, years: [prevYY, `20${prevYY}`], sw: WINTER_SW, endYears: [currYY, `20${currYY}`] },
    { key: 'curr_h',  label: `${currYY}년 하작물`,           short: `${currYY}년`,           years: [currYY, `20${currYY}`], sw: SUMMER_SW },
    { key: 'curr_d',  label: `${currYY}~${nextYY}년 동작물`, short: `${currYY}~${nextYY}년`, years: [currYY, `20${currYY}`], sw: WINTER_SW, endYears: [nextYY, `20${nextYY}`] },
  ] as { key: string; label: string; short: string; years: string[]; sw: string[]; endYears?: string[] }[];

  /* 작기·상태 필터링과 상태 집계는 모두 서버가 한다(서버 페이징 전제).
     화면은 서버가 준 페이지만 그린다 — 클라이언트 필터를 남겨두면 페이지 안에서 또 걸러
     '20건 중 3건'처럼 어긋난다. */
  const displayedSchedules = schedules;

  /* 선택은 페이지를 넘어가도 유지한다 — 서버 페이징에서는 보이지 않는 행이 정상이라,
     예전처럼 '안 보이면 해제' 규칙을 쓰면 페이지를 넘길 때마다 선택이 풀린다.
     조회 조건이 바뀔 때만 초기화한다. */
  useEffect(() => { setSelectedIds([]); }, [scopeParams, filterStatus]);

  const submitBulkDates = async () => {
    const v = await bulkDateForm.validateFields();
    const payload: { ids: number[]; report_date?: string; tims_status?: string } = { ids: selectedIds };
    if (v.report_date) payload.report_date = dayjs(v.report_date).format(DATE_FORMAT);
    if (v.tims_status) payload.tims_status = dayjs(v.tims_status).format(DATE_FORMAT);
    if (!payload.report_date && !payload.tims_status) {
      message.warning('성적서발급일 또는 TIMS입력현황 중 하나 이상을 입력하세요.');
      return;
    }
    setBulkDateSaving(true);
    try {
      const { data } = await scheduleAPI.bulkDates(payload);
      message.success(data?.message || `${selectedIds.length}건에 반영되었습니다.`);
      setBulkDateOpen(false);
      bulkDateForm.resetFields();
      setSelectedIds([]);
      reload();
    } catch (err: any) {
      message.error(err.response?.data?.detail || '일자 일괄등록에 실패했습니다.');
    } finally {
      setBulkDateSaving(false);
    }
  };

  /* ── 검색 결과 요약 카드 ──
     시스템 전체 집계가 아니라 '지금 화면에 걸린 검색·필터 결과(displayedSchedules)'를 집계한다.
     상태 카드는 결과에 0건이어도 항상 STATUS_ALL 순서로 노출해 카드가 사라졌다 나타나지 않게 하고
     (클릭으로 상태 필터를 토글하므로 사라지면 해제할 카드가 없어진다),
     STATUS_ALL 밖의 값(마스터에서 추가된 상태 등)은 뒤에 이어 붙인다. */
  const resultStatusCards = useMemo(() => {
    const order = [...STATUS_ALL as readonly string[]];
    Object.keys(statusCounts).forEach(k => { if (!order.includes(k)) order.push(k); });
    return order.map(name => ({
      name,
      count: statusCounts[name] || 0,
      pct: scopedTotal ? Math.round(((statusCounts[name] || 0) / scopedTotal) * 100) : 0,
      color: statusTagColors[name] || '#64748b',
      retired: RETIRED_STATUSES.has(name),
    }));
  }, [statusCounts, scopedTotal]);

  /* 시험년도 옵션 — 데이터에 있는 연도 + 현재 선택값(데이터가 아직 없는 연도 대비) */
  const yearOptions = useMemo(() => {
    const raws = Array.from(new Set([...filters.years, ...filterYears])).filter(Boolean);
    return raws
      .map(y => ({ raw: y, full: y.length === 2 ? `20${y}` : y }))
      .sort((a, b) => b.full.localeCompare(a.full));
  }, [filters.years, filterYears]);

  /* 현재 걸려 있는 검색·필터를 사람이 읽는 문장으로 (요약 카드 부제) */
  const activeFilterSummary = useMemo(() => {
    const parts: string[] = [];
    if (hasPeriod) {
      parts.push(filterPeriods
        .map(k => PERIOD_PRESETS.find(p => p.key === k)?.label || k)
        .join(' + '));
    }
    else if (filterYears.length) {
      parts.push(filterYears
        .map(y => (y.length === 2 ? `20${y}` : y))
        .sort((a, b) => b.localeCompare(a))
        .map(y => `${y}년`)
        .join('·'));
    }
    if (filterCategory) parts.push(filters.categories.find(c => c.id === filterCategory)?.name || '구분');
    if (filterRequestType) parts.push(filterRequestType);
    if (filterCompany) parts.push(filters.companies.find(c => c.id === filterCompany)?.name || '의뢰사');
    if (filterStatus) parts.push(filterStatus);
    if (search) parts.push(`"${search}"`);
    return parts.length ? parts.join(' · ') : '전체';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterPeriods, hasPeriod, filterYears, filterCategory, filterCompany, filterRequestType, filterStatus, search, filters]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>

      {/* ── 검색 결과 요약 (히어로 건수 + 진행상태 파이프라인 스택바) ──
           진행상태는 '부분의 합 = 전체'인 순서형 파이프라인이므로 타일 7개가 아니라
           가로 100% 스택바 하나로 표현한다. 색만으로 구분하지 않도록 범례에
           상태명·건수·%를 항상 문자로 적는다(상태색 일부가 대비 3:1 미만). */}
      <div style={{
        background: '#fff', borderRadius: 8, padding: '10px 12px',
        border: '1px solid #e2e8f0', boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
        position: 'relative',
      }}>
            {/* 머리줄 — 건수·조건·상태필터를 한 줄에 둔다.
                2026-09-03 이전에는 히어로 숫자(40px)가 왼쪽 열을 통째로 차지하고
                범례가 남은 폭(934px)에서 2줄로 접혀 카드가 125px 였다.
                전폭 3줄(머리줄 / 스택바 / 범례)로 바꾸면 범례가 한 줄에 들어가 93px 가 된다.
                건수는 여전히 이 카드에서 가장 큰 글자라 먼저 읽힌다 — 40px 이 필요했던 게 아니다 */}
            <div style={{
              display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap',
              paddingRight: 18,   /* 우상단 도움말 아이콘 자리 */
            }}>
              <span style={{ fontSize: 11, color: gray[400], fontWeight: 600, letterSpacing: '0.04em' }}>
                검색 결과
              </span>
              {/* 건수는 tabular-nums 를 쓰지 않는다 — 큰 글자에서 자간이 벌어져 보인다 */}
              <span style={{ fontSize: 20, fontWeight: 700, color: gray[800], lineHeight: 1.15 }}>
                {scopedTotal}
              </span>
              <span style={{ fontSize: 12, fontWeight: 500, color: gray[400], marginLeft: -4 }}>건</span>
              <span style={{ color: '#e2e8f0' }}>·</span>
              <span style={{ fontSize: 11, color: gray[400], flex: 1, minWidth: 80,
                             whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                    title={activeFilterSummary}>
                {activeFilterSummary}
              </span>
              {filterStatus && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 500 }}>
                  <span style={{
                    width: 7, height: 7, borderRadius: '50%',
                    background: statusTagColors[filterStatus] || '#64748b',
                  }} />
                  <span style={{ color: gray[600] }}>{filterStatus} {displayedSchedules.length}건만 표시 중</span>
                  <span role="button" tabIndex={0}
                    onClick={() => setFilterStatus('')}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setFilterStatus(''); } }}
                    style={{ cursor: 'pointer', color: gray[400], marginLeft: 2 }}>✕</span>
                </span>
              )}
            </div>

            {/* 스택바 — 세그먼트 사이 2px 표면 간격, 양끝만 라운드 */}
            <div style={{
              display: 'flex', gap: 2, height: 14, borderRadius: 4, marginTop: 8,
              background: '#f1f5f9', overflow: 'hidden',
            }}>
              {resultStatusCards.filter(s => s.count > 0).map(s => {
                const dim = !!filterStatus && filterStatus !== s.name;
                return (
                  <Tooltip key={s.name} title={`${s.name} ${s.count}건 (${s.pct}%)`}>
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => setFilterStatus(filterStatus === s.name ? '' : s.name)}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault(); setFilterStatus(filterStatus === s.name ? '' : s.name);
                        }
                      }}
                      style={{
                        flex: `${s.count} 0 0`, minWidth: 3, background: s.color,
                        opacity: dim ? 0.25 : 1, cursor: 'pointer', transition: 'opacity .15s',
                      }}
                    />
                  </Tooltip>
                );
              })}
            </div>

            {/* 범례 — 색만으로 구분하지 않도록 상태명·건수·%를 문자로 함께 적는다.
                텍스트는 잉크 색을 쓰고, 정체성은 옆의 색 점이 담당한다. */}
            <div style={{
              display: 'flex', flexWrap: 'wrap', gap: '4px 14px', marginTop: 8,
            }}>
              {resultStatusCards.map(s => {
                const active = filterStatus === s.name;
                const zero = s.count === 0;
                return (
                  <span
                    key={s.name}
                    role="button"
                    tabIndex={0}
                    onClick={() => setFilterStatus(active ? '' : s.name)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault(); setFilterStatus(active ? '' : s.name);
                      }
                    }}
                    title={`${s.name}${s.retired ? ' (사용중지)' : ''} · ${s.count}건 (${s.pct}%) — 클릭하면 이 상태만 조회합니다`}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 5,
                      padding: '2px 8px 2px 6px', borderRadius: 20, cursor: 'pointer',
                      background: active ? '#f1f5f9' : 'transparent',
                      boxShadow: active ? `inset 0 0 0 1px ${s.color}` : 'none',
                      opacity: zero && !active ? 0.45 : 1, transition: 'all .15s',
                    }}
                  >
                    <span style={{
                      width: 8, height: 8, borderRadius: '50%', background: s.color, flexShrink: 0,
                    }} />
                    <span style={{ fontSize: 12, color: gray[600], fontWeight: active ? 700 : 500 }}>
                      {s.name}
                    </span>
                    <span style={{ fontSize: 12, color: gray[800], fontWeight: 700 }}>{s.count}</span>
                    <span style={{ fontSize: 11, color: gray[400] }}>({s.pct}%)</span>
                    {s.retired && <span style={{ fontSize: 10, color: gray[400] }}>중지</span>}
                  </span>
                );
              })}
            </div>

        <Tooltip
          title={`현재 검색·필터 조건에 걸린 시험일정 건수와 진행상태 분포입니다. 조건: ${activeFilterSummary}. 막대와 범례를 클릭하면 해당 상태만 조회하고, 다시 누르면 해제됩니다. 시스템 전체 등록 건수는 ${stats?.total || 0}건입니다.`}
          overlayStyle={{ maxWidth: 300 }} placement="topRight">
          <QuestionCircleOutlined style={{
            position: 'absolute', top: 8, right: 10,
            fontSize: 11, color: gray[300], cursor: 'pointer',
          }} />
        </Tooltip>
      </div>

      {/* ── 메인 카드 ── */}
      <div ref={cardWrapRef}>
      <Card bodyStyle={{ padding: '0' }} style={{ borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        <Tabs activeKey={activeTab} onChange={setActiveTab}
          tabBarStyle={{ margin: 0, padding: '0 20px', background: '#fafbfc', borderBottom: '1px solid #e2e8f0' }}
          items={[
          { key: 'table', label: <span style={{ fontSize: 13, fontWeight: 500 }}><CalendarOutlined style={{ marginRight: 6 }} />시험일정 목록</span>, children: (
            /* 하단 패딩 0 — 페이지네이션이 자기 여백(12px)만 갖고 다른 목록 화면과 같은 높이에 선다 */
            <div style={{ padding: '16px 20px 0' }}>

              {/* ── 검색·필터 패널 ── */}
              <div style={{
                background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10,
                padding: '14px 16px', marginBottom: 14,
              }}>
                {/* 상단: 검색 + 액션 버튼 */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                  <Input
                    placeholder="시험번호, 항목명, 포장주소 검색..."
                    prefix={<SearchOutlined style={{ color: gray[400] }} />}
                    value={searchText}
                    onChange={e => setSearchText(e.target.value)}
                    allowClear
                    style={{ width: 300, borderRadius: 8 }}
                  />
                  {/* 필터 초기화는 이 줄 가운데 빈 자리로 옮겼다 — 필터 줄에 두면
                      작기 알약이 들어온 뒤로는 항상 둘째 줄로 밀린다.
                      'N건 조회됨'은 없앴다: 페이지네이션 푸터의 '1–20 / 총 250건'과
                      같은 정보를 두 곳에서 말하고 있었고, 1366px 에서는 이 한 조각 때문에
                      검색 줄까지 두 줄로 넘쳐 본문이 40px 밀렸다. */}
                  {activeFilterCount > 0 && (
                    <Button
                      size="small" type="link" danger
                      icon={<CloseCircleOutlined />}
                      onClick={clearAllFilters}
                      style={{ fontSize: 12, padding: '0 6px' }}
                    >
                      필터 초기화 ({activeFilterCount})
                    </Button>
                  )}
                  <div style={{ flex: 1 }} />
                  <Upload accept=".xlsx,.xls" showUploadList={false} beforeUpload={(file) => { handleUpload(file); return false; }}>
                    <Button icon={<FileExcelOutlined />} size="middle" style={{ borderRadius: 8, borderColor: '#10b981', color: '#10b981' }}>
                      Excel 업로드
                    </Button>
                  </Upload>
                  <Button icon={<DownloadOutlined />} size="middle" onClick={handleExport}
                    style={{ borderRadius: 8, borderColor: brand.primary, color: brand.primary, fontWeight: 600 }}>
                    Excel 다운로드
                  </Button>
                  <Button icon={<FileExcelOutlined />} size="middle" onClick={handleTemplateDownload}
                    style={{ borderRadius: 8, borderColor: '#6366f1', color: '#6366f1' }}>
                    양식 다운로드
                  </Button>
                  <Button icon={<PlusOutlined />} type="primary" size="middle" onClick={openNew}
                    style={{ borderRadius: 8, background: 'linear-gradient(135deg,#4f46e5,#6366f1)', border: 'none', fontWeight: 600 }}>
                    신규 등록
                  </Button>
                  <Tooltip title="새로고침">
                    <Button icon={<ReloadOutlined />} size="middle" onClick={fetchData} style={{ borderRadius: 8 }} />
                  </Tooltip>
                </div>

                {/* 구분선 */}
                <Divider style={{ margin: '0 0 12px', borderColor: '#e2e8f0' }} />

                {/* 하단: 필터 셀렉터 */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  {/* ── 작기 퀵필터 ── 별도 줄(65px)이던 것을 필터 줄로 합쳤다 (2026-08-30).
                      작기와 시험년도는 서로 배타적이라(작기를 고르면 연도 조회가 꺼진다)
                      같은 줄에 나란히 두는 편이 관계도 드러난다. */}
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600, color: gray[500] }}>
                    작기
                    <Tooltip
                      overlayStyle={{ maxWidth: 340 }}
                      placement="bottomLeft"
                      title={
                        <div style={{ fontSize: 12, lineHeight: 1.7 }}>
                          <div style={{ fontWeight: 700, marginBottom: 4 }}>작기(재배 시기)별 빠른 필터</div>
                          <div>☀️ <b>하작물</b> — 봄~가을에 개시·완결되는 당해년도 시험 (작기 하·S·E)</div>
                          <div>🧊 <b>동작물</b> — 가을에 시작해 이듬해 마무리되는 시험 (작기 동·W). 시즌이 2개년에 걸치므로 <b>시작연도~수확연도</b>로 표기합니다 (예: {currYY}~{nextYY}년).</div>
                          <div style={{ marginTop: 4, opacity: 0.85 }}>
                            · 시험년도가 수확연도({nextYY})로 등록된 동작기도 시험번호(SR{currYY})의 연도로 자동 포함됩니다.<br />
                            · 작기는 <b>여러 개를 함께 선택</b>할 수 있습니다 (선택한 작기 중 하나라도 맞으면 표시).<br />
                            · 작기가 입력되지 않은 시험은 '전체'에서만 표시됩니다.
                          </div>
                        </div>
                      }
                    >
                      <QuestionCircleOutlined style={{ fontSize: 12, color: gray[300], cursor: 'pointer' }} />
                    </Tooltip>
                  </span>
                  <button
                    onClick={() => { setFilterPeriods([]); setFilterYears([String(new Date().getFullYear()).slice(-2)]); }}
                    style={{
                      padding: '3px 11px', borderRadius: 14, fontSize: 11.5, cursor: 'pointer',
                      fontWeight: !hasPeriod ? 700 : 500, lineHeight: 1.6,
                      border: `1.5px solid ${!hasPeriod ? '#64748b' : '#e2e8f0'}`,
                      background: !hasPeriod ? '#334155' : '#fff',
                      color: !hasPeriod ? '#fff' : '#94a3b8',
                      transition: 'all 0.15s ease',
                    }}
                  >
                    전체
                  </button>
                  {[
                    { key: 'prev_h', isDong: false, color: '#d97706', hoverBg: '#fef3c7', gradBg: 'linear-gradient(135deg,#fbbf24,#d97706)' },
                    { key: 'prev_d', isDong: true,  color: brand.primary, hoverBg: '#eef2ff', gradBg: 'linear-gradient(135deg,#818cf8,#4f46e5)' },
                    { key: 'curr_h', isDong: false, color: '#059669', hoverBg: '#ecfdf5', gradBg: 'linear-gradient(135deg,#34d399,#059669)' },
                    { key: 'curr_d', isDong: true,  color: '#0369a1', hoverBg: '#e0f2fe', gradBg: 'linear-gradient(135deg,#38bdf8,#0369a1)' },
                  ].map(st => {
                    const preset = PERIOD_PRESETS.find(x => x.key === st.key)!;
                    const isActive = filterPeriods.includes(st.key);
                    return (
                      <button
                        key={st.key}
                        title={preset.label}
                        onClick={() => {
                          // 마지막 하나를 해제하면 '전체'로 돌아가므로 연도 필터를 올해로 되돌린다
                          const next = isActive
                            ? filterPeriods.filter(k => k !== st.key)
                            : [...filterPeriods, st.key];
                          setFilterPeriods(next);
                          setFilterYears(next.length ? [] : [String(new Date().getFullYear()).slice(-2)]);
                        }}
                        style={{
                          padding: '3px 9px', borderRadius: 14, fontSize: 11.5, cursor: 'pointer',
                          fontWeight: isActive ? 700 : 500, lineHeight: 1.6,
                          border: `1.5px solid ${isActive ? 'transparent' : st.color + '55'}`,
                          background: isActive ? st.gradBg : st.hoverBg,
                          color: isActive ? '#fff' : st.color,
                          boxShadow: isActive ? `0 2px 7px ${st.color}40` : 'none',
                          transition: 'all 0.15s ease',
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                        }}
                      >
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 2,
                          background: isActive ? 'rgba(255,255,255,0.25)' : (st.isDong ? '#dbeafe' : '#dcfce7'),
                          border: `1px solid ${isActive ? 'rgba(255,255,255,0.45)' : (st.isDong ? '#93c5fd' : '#86efac')}`,
                          borderRadius: 7, padding: '0 4px', fontSize: 9.5, fontWeight: 800,
                          color: isActive ? '#fff' : (st.isDong ? '#1d4ed8' : '#166534'), lineHeight: 1.6,
                        }}>
                          {st.isDong ? '동' : '하'}
                        </span>
                        {preset.short}
                        {isActive && <span style={{ fontSize: 9.5, opacity: 0.85 }}>✕</span>}
                      </button>
                    );
                  })}
                  <div style={{ width: 1, height: 18, background: '#cbd5e1', margin: '0 2px' }} />
                  <Tooltip title="상세 필터">
                    <FilterOutlined style={{ fontSize: 13, color: gray[400], marginRight: 2 }} />
                  </Tooltip>
                  {/* 시험년도 — N개 다중 선택. 비우면 전체 연도 */}
                  <Select
                    mode="multiple"
                    placeholder="📅 시험년도 (전체)"
                    value={filterYears}
                    onChange={(v: string[]) => setFilterYears(v || [])}
                    allowClear size="small"
                    maxTagCount="responsive"
                    maxTagPlaceholder={omitted => (omitted.length ? `+${omitted.length}` : '')}
                    /* minWidth 를 124 로 두면 선택된 태그('2026년 ✕')의 삭제 ✕ 가 컨트롤의
                       가로 정중앙에 놓여, 가운데를 클릭하면 목록이 열리는 대신 연도 필터가 지워진다
                       (2026-09-03 밀도 키트 적용 후 실측 — 화면테스트 filter_interact 가 잡았다).
                       태그가 중앙에 닿지 않도록 폭을 넓힌다 */
                    style={{ minWidth: 156, maxWidth: 210, borderRadius: 8 }}
                    disabled={hasPeriod}
                    title={hasPeriod ? '작기 필터가 켜져 있으면 작기 기준으로 조회합니다' : undefined}
                  >
                    {yearOptions.map(({ raw, full }) => <Option key={raw} value={raw}>{`${full}년`}</Option>)}
                  </Select>
                  <Select
                    placeholder="🏷 구분"
                    value={filterCategory || undefined}
                    onChange={v => setFilterCategory(v || 0)}
                    allowClear size="small"
                    style={{ width: 132, borderRadius: 8 }}
                  >
                    {filters.categories
                      .filter(c => ['품목등록-위탁','품목등록-자체','기초시험','지역적응성시험'].includes(c.name))
                      .map(c => {
                        const label = c.name.startsWith('품목등록-') ? c.name.slice(5)
                          : c.name === '지역적응성시험' ? '지역적응성' : c.name;
                        return <Option key={c.id} value={c.id}>{label}</Option>;
                      })}
                  </Select>
                  <Select
                    placeholder="🏛️ 의뢰구분"
                    value={filterRequestType || undefined}
                    onChange={v => setFilterRequestType(v || '')}
                    allowClear size="small"
                    style={{ width: 106, borderRadius: 8 }}
                  >
                    <Option value="협회"><RequestTypeBadge value="협회" /></Option>
                    <Option value="비협회"><RequestTypeBadge value="비협회" /></Option>
                  </Select>
                  <Select
                    placeholder="🏢 의뢰사"
                    value={filterCompany || undefined}
                    onChange={v => setFilterCompany(v || 0)}
                    allowClear size="small"
                    style={{ width: 112, borderRadius: 8 }}
                  >
                    {filters.companies.map(c => <Option key={c.id} value={c.id}>{c.name}</Option>)}
                  </Select>
                  <Select
                    placeholder="🔵 상태"
                    value={filterStatus || undefined}
                    onChange={v => setFilterStatus(v || '')}
                    allowClear size="small"
                    style={{ width: 116, borderRadius: 8 }}
                  >
                    {STATUS_LIST.map(s => (
                      <Option key={s} value={s}>
                        <span style={{ display:'inline-flex', alignItems:'center', gap:6 }}>
                          <span style={{ width:8, height:8, borderRadius:'50%', background: statusTagColors[s], display:'inline-block', flexShrink:0 }} />
                          {s}
                          {RETIRED_STATUSES.has(s) && (
                            <span style={{ fontSize: 10, color: gray[400] }}>(사용중지)</span>
                          )}
                        </span>
                      </Option>
                    ))}
                  </Select>

                </div>
              </div>

              {/* ── 테이블 ──
                   선택 툴바는 흐름에 끼워 넣지 않고 표 위에 떠 있게 한다.
                   흐름에 넣으면 체크할 때마다 표 높이가 밀려 화면이 들썩인다. */}
              <div ref={tableWrapRef} style={{ position: 'relative' }}>
              <Table
                dataSource={displayedSchedules}
                columns={columns}
                rowKey="id"
                loading={loading}
                size="small"
                rowSelection={{
                  selectedRowKeys: selectedIds,
                  onChange: (keys) => setSelectedIds(keys as number[]),
                  columnWidth: 40,
                  fixed: true,
                }}
                showSorterTooltip={false}
                scroll={{ x: 1380, y: bodyHeight }}
                sticky
                rowClassName={(_, index) => index % 2 === 0 ? '' : 'table-row-alt'}
                locale={{ emptyText: <EmptyState title="등록된 시험일정이 없습니다" size="sm" /> }}
                style={{ borderRadius: 8, overflow: 'hidden' }}
                /* 서버 페이징 — 정렬도 서버가 한다. 클라이언트 sorter 를 남기면
                   현재 페이지 20건 안에서만 정렬돼 전체 정렬과 어긋난다 */
                pagination={{
                  current: page, pageSize, total, showSizeChanger: true,
                  pageSizeOptions: ['20','50','100','200'],
                  showTotal: (t, r) => `${r[0]}–${r[1]} / 총 ${t}건`,
                }}
                onChange={(pg, _f, sorter: any) => {
                  const s0 = Array.isArray(sorter) ? sorter[0] : sorter;
                  const key = s0?.order ? String(s0.columnKey || s0.field || '') : '';
                  const dir = s0?.order === 'descend' ? 'desc' : 'asc';
                  const sortChanged = key !== sortBy || dir !== sortDir;
                  setSortBy(key);
                  setSortDir(dir);
                  setPageSize(pg.pageSize || 20);
                  /* 정렬이 바뀌면 1페이지로 — 3페이지에서 정렬을 바꾸면 전혀 다른 구간이 나와
                     사용자는 '정렬이 안 먹었다'고 읽는다 */
                  setPage(sortChanged ? 1 : (pg.current || 1));
                }}
              />

              {/* 선택 n건 일괄작업 — 표 위에 떠 있는 바 (레이아웃 밀림 없음) */}
              {selectedIds.length > 0 && (
                <div className="bulk-select-bar">
                  <span className="bulk-select-count">{selectedIds.length}</span>
                  <Typography.Text className="bulk-select-label">건 선택됨</Typography.Text>
                  <Button
                    type="primary" icon={<FileDoneOutlined />}
                    onClick={() => { bulkDateForm.resetFields(); setBulkDateOpen(true); }}
                    className="bulk-select-btn"
                  >
                    일자 일괄등록
                  </Button>
                  <Tooltip title="선택 해제">
                    <Button type="text" icon={<CloseOutlined />} className="bulk-select-close"
                      onClick={() => setSelectedIds([])} />
                  </Tooltip>
                </div>
              )}
            </div>
            </div>
          )},
          { key: 'stats', label: <span style={{ fontSize: 13, fontWeight: 500 }}><BarChartOutlined style={{ marginRight: 6 }} />통계 분석</span>, children: stats ? (
            <div style={{ padding: '16px 20px' }}>
              {/* 의뢰구분 → 의뢰사 순서 (목록·필터·폼·상세와 동일) */}
              <Row gutter={[16,16]}>
                <Col xs={24} lg={8}><Card size="small" style={{ borderRadius: 10 }}><ReactECharts option={requestTypeChart} style={{ height: 320 }} /></Card></Col>
                <Col xs={24} lg={16}><Card size="small" style={{ borderRadius: 10 }}><ReactECharts option={companyChart} style={{ height: 320 }} /></Card></Col>
                <Col xs={24} lg={12}><Card size="small" style={{ borderRadius: 10 }}><ReactECharts option={categoryChart} style={{ height: 350 }} /></Card></Col>
                <Col xs={24} lg={12}><Card size="small" style={{ borderRadius: 10 }}><ReactECharts option={locationChart} style={{ height: 350 }} /></Card></Col>
              </Row>
            </div>
          ) : null },
        ]} />
      </Card>
      </div>

      {/* 상세보기 모달 */}
      <Modal
        title={detailRecord ? (
          <Space>
            <Typography.Text strong style={{ color: brand.primary }}>{detailRecord.test_number}</Typography.Text>
            <Typography.Text>{detailRecord.title}</Typography.Text>
          </Space>
        ) : ''}
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={null}
        width={820}
        maskClosable={false}
        styles={{ body: { maxHeight: '72vh', overflowY: 'auto', overflowX: 'hidden', paddingRight: 4 } }}
      >
        {detailRecord && (() => {
          const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: gray[400], marginBottom: 3, fontWeight: 500 }}>{label}</div>
              <div style={{ fontSize: 13, color: gray[800], minHeight: 20 }}>{children || <span style={{ color: '#cbd5e1' }}>-</span>}</div>
            </div>
          );
          const SectionBar = ({ icon, label, color, bg }: { icon: React.ReactNode; label: string; color: string; bg: string }) => (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', margin: '0 0 14px', background: bg, borderLeft: `3px solid ${color}`, borderRadius: '0 6px 6px 0' }}>
              {icon}<span style={{ fontWeight: 600, fontSize: 12, color }}>{label}</span>
            </div>
          );
          return (
            <div style={{ padding: '4px 0' }}>
              {/* ── 기본 정보 ── */}
              <SectionBar icon={<ExperimentOutlined style={{ color: brand.primary, fontSize: 13 }} />} label="기본 정보" color="#4f46e5" bg="#eef2ff" />
              <Row gutter={16}>
                <Col flex="80px"><Field label="연번">{detailRecord.seq_no ?? '-'}</Field></Col>
                <Col flex="1"><Field label="시험번호"><Typography.Text strong style={{ color: brand.primary }}>{detailRecord.test_number}</Typography.Text></Field></Col>
                <Col flex="120px"><Field label="시험년도">{detailRecord.test_year || '-'}</Field></Col>
              </Row>
              <Field label="항목명"><span style={{ fontWeight: 600 }}>{detailRecord.title}</span></Field>
              {/* 의뢰구분 → 의뢰사 순서 (목록·필터·폼과 동일). 이전에는 '의뢰사' 한 칸에
                  의뢰구분 배지가 섞여 있어 라벨과 내용이 어긋났다 */}
              <Row gutter={16}>
                <Col span={4}><Field label="의뢰구분">
                  <RequestTypeBadge value={detailRecord.request_type} size="md" />
                </Field></Col>
                <Col span={8}><Field label="의뢰사">
                  <Space size={4} wrap>
                    {(detailRecord.companies?.length
                      ? detailRecord.companies
                      : (detailRecord.company_name ? [{ id: 0, name: detailRecord.company_name, is_primary: true }] : [])
                    ).map((c, i) => (
                      <Tag key={c.id || i} color={c.is_primary ? 'blue' : undefined}
                        style={{ fontWeight: 600, marginRight: 0, ...(c.is_primary ? {} : { color: '#6366f1', borderColor: '#c7d2fe', background: '#eef2ff' }) }}>
                        {c.name}
                      </Tag>
                    ))}
                    {!detailRecord.companies?.length && !detailRecord.company_name && '-'}
                  </Space>
                </Field></Col>
                <Col span={6}><Field label="구분"><Tag style={{ fontWeight: 600, borderColor: getCategoryColor(detailRecord.category_name), color: getCategoryColor(detailRecord.category_name), background: getCategoryColor(detailRecord.category_name) + '1a' }}>{detailRecord.category_name || '-'}</Tag></Field></Col>
                <Col span={6}><Field label="상태"><Tag color={statusTagColors[detailRecord.status] || '#64748b'}>{detailRecord.status || '-'}</Tag></Field></Col>
              </Row>
              <Row gutter={16}>
                <Col span={9}><Field label="시험장소">{detailRecord.location_name || '-'}</Field></Col>
                <Col span={9}><Field label="포장주">{detailRecord.field_owner_name}{detailRecord.field_owner_phone ? ` (${detailRecord.field_owner_phone})` : ''}</Field></Col>
                <Col span={6}><Field label="개시일">{detailRecord.start_date || '-'}</Field></Col>
              </Row>
              <Row gutter={16}>
                <Col span={17}><Field label="시험포장주소">
                  <Space size={6}>
                    {detailRecord.field_address || '-'}
                    {detailRecord.field_address && (
                      <Tooltip title="네이버지도에서 검색">
                        <a href={`https://map.naver.com/v5/search/${encodeURIComponent(detailRecord.field_address)}`} target="_blank" rel="noopener noreferrer" style={{ color: '#03C75A', fontSize: 15 }}><EnvironmentOutlined /></a>
                      </Tooltip>
                    )}
                  </Space>
                </Field></Col>
                <Col span={7}><Field label="정식시기">{detailRecord.planting_date || '-'}</Field></Col>
              </Row>

              {/* ── 처리일정 ── */}
              <SectionBar icon={<CalendarOutlined style={{ color: '#7c3aed', fontSize: 13 }} />} label={`처리일정 (${(detailRecord.treatments || []).length}회)`} color="#7c3aed" bg="#f5f3ff" />
              <Table dataSource={detailRecord.treatments || []} rowKey="round_no" size="small" pagination={false} style={{ marginBottom: 16 }}
                columns={[
                  { title: '차수', dataIndex: 'round_no', width: 60, render: (v: number) => `${v}차` },
                  { title: '처리일자', dataIndex: 'treatment_date', width: 160 },
                  { title: '비고', dataIndex: 'note' },
                ]} />

              {/* ── 약효조사일정 ── */}
              {(() => {
                const yakho = (detailRecord.surveys || []).filter((s: any) => !s.survey_type || s.survey_type === '약효');
                return (<>
                  <SectionBar icon={<AuditOutlined style={{ color: '#0891b2', fontSize: 13 }} />} label={`약효조사일정 (${yakho.length}회)`} color="#0891b2" bg="#ecfeff" />
                  <Table dataSource={yakho} rowKey="id" size="small" pagination={false} style={{ marginBottom: 12 }}
                    columns={[
                      { title: '차수', dataIndex: 'round_no', width: 60, render: (v: number) => `${v}차` },
                      { title: '약효조사일자', dataIndex: 'survey_date', width: 160 },
                      { title: '비고', dataIndex: 'note' },
                    ]} />
                </>);
              })()}

              {/* ── 약해조사일정 ── */}
              {(() => {
                const yakhe = (detailRecord.surveys || []).filter((s: any) => s.survey_type === '약해');
                return (<>
                  <SectionBar icon={<AuditOutlined style={{ color: '#ea580c', fontSize: 13 }} />}
                    label={`약해조사일정 (${yakhe.length}회)`} color="#ea580c" bg="#fff7ed" />
                  <Table dataSource={yakhe} rowKey="id" size="small" pagination={false} style={{ marginBottom: 16 }}
                    columns={[
                      { title: '차수', dataIndex: 'round_no', width: 60, render: (v: number) => `${v}차` },
                      { title: '약해조사일자', dataIndex: 'survey_date', width: 160 },
                      { title: '비고', dataIndex: 'note' },
                    ]} />
                </>);
              })()}

              {/* ── 관리현황 ── */}
              <SectionBar icon={<FileDoneOutlined style={{ color: '#059669', fontSize: 13 }} />} label="관리현황" color="#059669" bg="#f0fdf4" />
              <Row gutter={16}>
                <Col span={6}><Field label="성적서발급일">{detailRecord.report_date || '-'}</Field></Col>
                <Col span={6}><Field label="TIMS입력현황">{detailRecord.tims_status || '-'}</Field></Col>
                <Col span={6}><Field label="정리">{detailRecord.cleanup || '-'}</Field></Col>
                <Col span={6}><Field label="검토기록지">{detailRecord.review_record || '-'}</Field></Col>
              </Row>
              {/* 성적서사진유무 — 수정 폼과 동일한 박스 레이아웃 (읽기 전용) */}
              {(() => {
                const val = detailRecord.has_result_photo;
                const isTrue = val === true;
                const isFalse = val === false;
                const boxBg = isTrue ? '#f0fdf4' : isFalse ? '#fef2f2' : '#f8fafc';
                const boxBorder = isTrue ? '#bbf7d0' : isFalse ? '#fecaca' : '#e2e8f0';
                return (
                  <div style={{
                    background: boxBg, border: `1px solid ${boxBorder}`,
                    borderRadius: 8, padding: '7px 12px', display: 'flex',
                    alignItems: 'center', gap: 8, marginBottom: 12,
                  }}>
                    <span style={{ fontSize: 13, color: gray[600], fontWeight: 500, flexShrink: 0 }}>성적서사진유무</span>
                    {isTrue || isFalse ? (
                      <span style={{
                        padding: '3px 12px', borderRadius: 5, fontSize: 12, lineHeight: '20px',
                        border: `1.5px solid ${isTrue ? '#22c55e' : '#ef4444'}`,
                        background: isTrue ? '#dcfce7' : '#fee2e2',
                        color: isTrue ? '#16a34a' : '#dc2626',
                        fontWeight: 700,
                      }}>{isTrue ? '있음' : '없음'}</span>
                    ) : (
                      <span style={{ fontSize: 12, color: '#94a3b8' }}>미지정</span>
                    )}
                  </div>
                );
              })()}
              {detailRecord.qrcode && (
                <div style={{ marginTop: 8, padding: '12px 16px', background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                  <div style={{ fontSize: 11, color: gray[500], marginBottom: 8, fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: 1 }}>
                    Dynamic QR 코드
                  </div>
                  <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                    <img
                      src={qrAPI.imageUrl(detailRecord.qrcode)}
                      alt="QR"
                      style={{ width: 100, height: 100, border: '1px solid #e2e8f0', borderRadius: 6 }}
                    />
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <div>
                        <span style={{ fontSize: 11, color: gray[400] }}>Short Code: </span>
                        <span style={{ fontFamily: font.mono, fontSize: 14, fontWeight: 700, color: brand.primary, letterSpacing: 2 }}>
                          {detailRecord.qrcode}
                        </span>
                      </div>
                      <div style={{ fontSize: 11, color: gray[400] }}>
                        api.sappt.co.kr/api/qr/{detailRecord.qrcode}
                      </div>
                      <Space size={6} style={{ marginTop: 2 }}>
                        <Button
                          size="small"
                          icon={<DownloadOutlined />}
                          onClick={() => {
                            const a = document.createElement('a');
                            a.href = qrAPI.imageUrl(detailRecord.qrcode);
                            a.download = `qr_${detailRecord.qrcode}.png`;
                            a.click();
                          }}
                        >
                          다운로드
                        </Button>
                        <Button
                          size="small"
                          icon={<PrinterOutlined />}
                          type="primary"
                          ghost
                          onClick={() => {
                            const win = window.open('', '_blank', 'width=400,height=500');
                            if (!win) return;
                            win.document.write(`<!DOCTYPE html><html><head><title>QR 포장 표지판</title>
                              <style>body{font-family:sans-serif;display:flex;align-items:center;justify-content:center;min-height:100vh;margin:0;}
                              .label{border:2px solid #1e293b;border-radius:12px;padding:24px 32px;text-align:center;width:280px;}
                              .num{font-size:13px;color:#64748b;margin-bottom:4px;} .title{font-size:16px;font-weight:700;color:#1e293b;margin-bottom:16px;}
                              img{width:160px;height:160px;} .code{font-size:11px;color:#94a3b8;margin-top:8px;font-family:var(--font-mono, ui-monospace, 'SFMono-Regular', Menlo, Consolas, monospace);}
                              @media print{body{margin:0;}}</style></head><body>
                              <div class="label">
                                <div class="num">${escapeHtml(detailRecord.test_number)}</div>
                                <div class="title">${escapeHtml(detailRecord.title)}</div>
                                <img src="${escapeHtml(window.location.origin + qrAPI.imageUrl(detailRecord.qrcode))}" alt="QR"/>
                                <div class="code">api.sappt.co.kr/api/qr/${escapeHtml(detailRecord.qrcode)}</div>
                              </div>
                              <script>window.onload=()=>{window.print();}<\/script></body></html>`);
                            win.document.close();
                          }}
                        >
                          포장 표지판 인쇄
                        </Button>
                      </Space>
                    </div>
                  </div>
                </div>
              )}

              {/* ── 파트너 문의/요청 (상세에서 바로 답변) ── */}
              <div style={{
                marginTop: 16, borderTop: `1px solid ${gray[100]}`, paddingTop: 14,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                  <MessageOutlined style={{ color: brand.primary }} />
                  <span style={{ fontWeight: 700, fontSize: 13, color: gray[700] }}>파트너 문의 / 요청</span>
                  {(() => {
                    const st = inquiryStats[detailRecord.id];
                    if (!st?.total) return <Tag style={{ margin: 0, fontSize: 11 }}>0건</Tag>;
                    return (
                      <Space size={4}>
                        <Tag style={{ margin: 0, fontSize: 11 }}>{st.total}건</Tag>
                        {st.waiting > 0 && (
                          <Tag color="orange" style={{ margin: 0, fontSize: 11, fontWeight: 700 }}>
                            답변대기 {st.waiting}
                          </Tag>
                        )}
                      </Space>
                    );
                  })()}
                </div>
                <InquiryPanel
                  key={detailRecord.id}
                  scheduleId={detailRecord.id}
                  onChanged={loadInquiryStats}
                />
              </div>
            </div>
          );
        })()}
      </Modal>

      {/* 목록 문의 배지 → 시험별 문의/요청 패널 */}
      <Modal
        open={!!inquiryRecord}
        onCancel={() => { setInquiryRecord(null); loadInquiryStats(); }}
        footer={<Button onClick={() => { setInquiryRecord(null); loadInquiryStats(); }}>닫기</Button>}
        width={720}
        title={
          <Space>
            <MessageOutlined style={{ color: brand.primary }} />
            <span style={{ fontWeight: 700 }}>파트너 문의 / 요청</span>
            {inquiryRecord && <Tag color="blue" style={{ margin: 0 }}>{inquiryRecord.test_number}</Tag>}
          </Space>
        }
        styles={{ body: { maxHeight: '68vh', overflowY: 'auto' } }}
      >
        {inquiryRecord && (
          <InquiryPanel
            key={inquiryRecord.id}
            scheduleId={inquiryRecord.id}
            onChanged={loadInquiryStats}
          />
        )}
      </Modal>

      {/* 등록/수정 모달 */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{
              width: 4, height: 18, borderRadius: 2,
              background: editingId ? '#4f46e5' : '#059669', flexShrink: 0,
            }} />
            <span style={{ fontSize: 15, fontWeight: 700, color: gray[800] }}>
              {editingId ? '시험일정 수정' : '시험일정 신규등록'}
            </span>
          </div>
        }
        open={formVisible}
        onCancel={() => { setFormVisible(false); setEditingId(null); form.resetFields(); }}
        onOk={handleFormSubmit}
        width={900}
        okText={editingId ? '수정 저장' : '등록'}
        cancelText="취소"
        maskClosable={false}
        styles={{ body: { padding: '16px 20px' } }}
      >
        <Form form={form} layout="vertical" size="small" style={{ maxHeight: '78vh', overflow: 'auto', paddingRight: 4 }}>

          {/* ── 기본정보 ── */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '4px 10px', margin: '0 0 8px',
            background: '#eef2ff', borderLeft: '3px solid #4f46e5', borderRadius: '0 6px 6px 0',
          }}>
            <ExperimentOutlined style={{ color: brand.primary, fontSize: 12 }} />
            <span style={{ fontWeight: 600, fontSize: 11, color: brand.primary }}>기본 정보</span>
          </div>

          {/* Row 1: 연번(60) | 시험번호(flex) | 시험년도(68) | 작기(92) | 상태(130) */}
          <Row gutter={8} align="bottom">
            <Col flex="60px">
              <Form.Item name="seq_no" label="연번" style={{ marginBottom: 8 }}>
                <InputNumber style={{ width: '100%' }} min={1} />
              </Form.Item>
            </Col>
            <Col flex="1">
              <Form.Item name="test_number" label="시험번호" rules={[{ required: true }]} style={{ marginBottom: 8 }}>
                <Input />
              </Form.Item>
            </Col>
            <Col flex="68px">
              <Form.Item name="test_year" label="시험년도" style={{ marginBottom: 8 }}>
                <Input placeholder="25" />
              </Form.Item>
            </Col>
            <Col flex="92px">
              <Form.Item name="summer_winter" label="작기" style={{ marginBottom: 8 }}>
                <Select allowClear placeholder="선택">
                  <Option value="하">☀️ 하</Option>
                  <Option value="동">🧊 동</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col flex="128px">
              <Form.Item name="status" label="상태" initialValue="시작전" style={{ marginBottom: 8 }}>
                <Select>
                  {/* 사용중지 상태는 선택 불가(disabled) — 기존에 그 상태인 시험은 값이 그대로 표시되고 다른 상태로 변경만 가능 */}
                  {STATUS_LIST.map(s => (
                    <Option key={s} value={s} disabled={RETIRED_STATUSES.has(s)}>
                      <Tag color={statusTagColors[s]} style={{ fontSize: 11 }}>{s}</Tag>
                      {RETIRED_STATUSES.has(s) && (
                        <span style={{ fontSize: 10, color: gray[400] }}>사용중지</span>
                      )}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {/* Row 2: 항목명 */}
          <Form.Item name="title" label="항목명" rules={[{ required: true }]} style={{ marginBottom: 8 }}>
            <Input />
          </Form.Item>

          {/* Row 3: 의뢰구분(4) | 의뢰사 다중선택(8) | 구분(6) | 시험장소(6) — 화면 전역 '의뢰구분 → 의뢰사' 순서 */}
          <Row gutter={8}>
            <Col span={4}>
              <Form.Item name="request_type" label="의뢰구분" initialValue="비협회" style={{ marginBottom: 8 }}>
                <Select>
                  <Option value="협회"><RequestTypeBadge value="협회" /></Option>
                  <Option value="비협회"><RequestTypeBadge value="비협회" /></Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="company_ids"
                label={<span>의뢰사 <Typography.Text type="secondary" style={{ fontSize: 10 }}>(복수 선택 · 첫번째=대표)</Typography.Text></span>}
                style={{ marginBottom: 8 }}
              >
                <Select mode="multiple" allowClear showSearch placeholder="선택 (복수 가능)"
                  maxTagCount={2}
                  filterOption={(input, option) => String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                  options={filters.companies.map(c => ({
                    value: c.id,
                    label: (c.code ? `${c.code} - ${c.name}` : c.name) + (c.company_type === '협회' ? ' 🏛️' : ''),
                  }))}
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="category_id" label="구분" style={{ marginBottom: 8 }}>
                <Select allowClear placeholder="선택">
                  {filters.categories.map(c => <Option key={c.id} value={c.id}>{c.name}</Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="location_id" label="시험장소" style={{ marginBottom: 8 }}>
                <Select allowClear showSearch placeholder="선택"
                  filterOption={(input, option) => (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())}>
                  {filters.locations.map(l => <Option key={l.id} value={l.id}>{l.name}</Option>)}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {/* Row 4: 포장주(9) | 정식시기(5) | 개시일(5) | 정리(5) */}
          <Row gutter={8}>
            <Col span={9}>
              <Form.Item name="field_owner_id" label="포장주" style={{ marginBottom: 8 }}>
                <Select allowClear showSearch placeholder="선택"
                  filterOption={(input, option) => (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())}>
                  {filters.owners.map(o => <Option key={o.id} value={o.id}>{o.name}{o.phone ? ` (${o.phone})` : ''}</Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item name="planting_date" label="정식시기" style={{ marginBottom: 8 }}>
                <DatePicker format={DATE_FORMAT} style={{ width: '100%' }} placeholder="날짜 선택" />
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item name="start_date" label="개시일" style={{ marginBottom: 8 }}>
                <Input placeholder="9월 상순" />
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item name="cleanup" label="정리" style={{ marginBottom: 8 }}>
                <Input />
              </Form.Item>
            </Col>
          </Row>

          {/* Row 5: 시험포장주소 (전체 폭) */}
          <Form.Item name="field_address" label="시험포장주소" style={{ marginBottom: 8 }}>
            <Input />
          </Form.Item>

          {/* ── 처리일정 ── */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '4px 10px', margin: '4px 0 8px',
            background: '#f5f3ff', borderLeft: '3px solid #7c3aed', borderRadius: '0 6px 6px 0',
          }}>
            <Space size={6}>
              <CalendarOutlined style={{ color: '#7c3aed', fontSize: 12 }} />
              <span style={{ fontWeight: 600, fontSize: 11, color: '#7c3aed' }}>처리일정</span>
            </Space>
            <Form.Item noStyle shouldUpdate={(p, c) => p.status !== c.status}>
              {() => {
                const cur = form.getFieldValue('status');
                return (
                  <Tooltip title={cur === '처리완료' ? '이미 처리완료 상태입니다' : '저장 후 상태를 처리완료로 변경합니다'}>
                    <Button size="small" icon={<CheckCircleOutlined />} loading={statusChanging}
                      disabled={!editingId || cur === '처리완료'}
                      onClick={() => handleQuickStatus('처리완료')}
                      style={{
                        borderRadius: 6, fontSize: 11, fontWeight: 600, height: 24,
                        background: cur === '처리완료' ? '#eff6ff' : '#3b82f6',
                        borderColor: cur === '처리완료' ? '#bfdbfe' : '#3b82f6',
                        color: cur === '처리완료' ? '#93c5fd' : '#fff',
                      }}>처리완료</Button>
                  </Tooltip>
                );
              }}
            </Form.Item>
          </div>
          <Form.List name="treatments">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...rest }) => (
                  <Row gutter={8} key={key} align="middle" style={{ marginBottom: 0 }}>
                    <Col flex="64px">
                      <Form.Item {...rest} name={[name, 'round_no']} label={key === 0 ? '차수' : undefined} style={{ marginBottom: 6 }}>
                        <InputNumber min={1} max={7} style={{ width: 56 }} />
                      </Form.Item>
                    </Col>
                    <Col flex="160px">
                      <Form.Item {...rest} name={[name, 'treatment_date']} label={key === 0 ? '처리일자' : undefined} rules={[{ required: true, message: '필수' }]} style={{ marginBottom: 6 }}>
                        <DateOrTextPicker width={148} />
                      </Form.Item>
                    </Col>
                    <Col flex="1">
                      <Form.Item {...rest} name={[name, 'note']} label={key === 0 ? '비고' : undefined} style={{ marginBottom: 6 }}>
                        <Input placeholder="비고" />
                      </Form.Item>
                    </Col>
                    <Col flex="28px">
                      <Button type="text" danger icon={<MinusCircleOutlined />} onClick={() => remove(name)}
                        style={{ marginTop: key === 0 ? 22 : 0, padding: '0 4px' }} />
                    </Col>
                  </Row>
                ))}
                <Button type="dashed" onClick={() => add({ round_no: fields.length + 1, treatment_date: null, note: '' })}
                  icon={<PlusOutlined />} style={{ width: '100%', marginBottom: 4, height: 26, fontSize: 12 }}>처리 추가</Button>
              </>
            )}
          </Form.List>

          {/* ── 약효조사일정 ── */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '4px 10px', margin: '6px 0 8px',
            background: '#ecfeff', borderLeft: '3px solid #0891b2', borderRadius: '0 6px 6px 0',
          }}>
            <Space size={6}>
              <AuditOutlined style={{ color: '#0891b2', fontSize: 12 }} />
              <span style={{ fontWeight: 600, fontSize: 11, color: '#0891b2' }}>약효조사일정</span>
            </Space>
            <Form.Item noStyle shouldUpdate={(p, c) => p.status !== c.status}>
              {() => {
                const cur = form.getFieldValue('status');
                return (
                  <Tooltip title={cur === '조사완료' ? '이미 조사완료 상태입니다' : '저장 후 상태를 조사완료로 변경합니다'}>
                    <Button size="small" icon={<CheckCircleOutlined />} loading={statusChanging}
                      disabled={!editingId || cur === '조사완료'}
                      onClick={() => handleQuickStatus('조사완료')}
                      style={{
                        borderRadius: 6, fontSize: 11, fontWeight: 600, height: 24,
                        background: cur === '조사완료' ? '#fffbeb' : '#f59e0b',
                        borderColor: cur === '조사완료' ? '#fde68a' : '#f59e0b',
                        color: cur === '조사완료' ? '#fbbf24' : '#fff',
                      }}>조사완료</Button>
                  </Tooltip>
                );
              }}
            </Form.Item>
          </div>
          <Form.List name="surveys_yakho">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...rest }) => (
                  <Row gutter={8} key={key} align="middle">
                    <Col flex="64px">
                      <Form.Item {...rest} name={[name, 'round_no']} label={key === 0 ? '차수' : undefined} style={{ marginBottom: 6 }}>
                        <InputNumber min={1} max={10} style={{ width: 56 }} />
                      </Form.Item>
                    </Col>
                    <Col flex="160px">
                      <Form.Item {...rest} name={[name, 'survey_date']} label={key === 0 ? '약효조사일자' : undefined} rules={[{ required: true, message: '필수' }]} style={{ marginBottom: 6 }}>
                        <DateOrTextPicker width={148} />
                      </Form.Item>
                    </Col>
                    <Col flex="1">
                      <Form.Item {...rest} name={[name, 'note']} label={key === 0 ? '비고' : undefined} style={{ marginBottom: 6 }}>
                        <Input placeholder="비고" />
                      </Form.Item>
                    </Col>
                    <Col flex="28px">
                      <Button type="text" danger icon={<MinusCircleOutlined />} onClick={() => remove(name)}
                        style={{ marginTop: key === 0 ? 22 : 0, padding: '0 4px' }} />
                    </Col>
                  </Row>
                ))}
                <Button type="dashed" onClick={() => add({ round_no: fields.length + 1, survey_date: null, note: '' })}
                  icon={<PlusOutlined />} style={{ width: '100%', marginBottom: 4, height: 26, fontSize: 12 }}>약효조사 추가</Button>
              </>
            )}
          </Form.List>

          {/* ── 약해조사일정 ── */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '4px 10px', margin: '6px 0 8px',
            background: '#fff7ed', borderLeft: '3px solid #ea580c', borderRadius: '0 6px 6px 0',
          }}>
            <Space size={6}>
              <AuditOutlined style={{ color: '#ea580c', fontSize: 12 }} />
              <span style={{ fontWeight: 600, fontSize: 11, color: '#ea580c' }}>약해조사일정</span>
            </Space>
            <span style={{ fontSize: 10, color: '#f97316' }}>선택 입력</span>
          </div>
          <Form.List name="surveys_yakhe">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...rest }) => (
                  <Row gutter={8} key={key} align="middle">
                    <Col flex="64px">
                      <Form.Item {...rest} name={[name, 'round_no']} label={key === 0 ? '차수' : undefined} style={{ marginBottom: 6 }}>
                        <InputNumber min={1} max={10} style={{ width: 56 }} />
                      </Form.Item>
                    </Col>
                    <Col flex="160px">
                      <Form.Item {...rest} name={[name, 'survey_date']} label={key === 0 ? '약해조사일자' : undefined} style={{ marginBottom: 6 }}>
                        <DateOrTextPicker width={148} />
                      </Form.Item>
                    </Col>
                    <Col flex="1">
                      <Form.Item {...rest} name={[name, 'note']} label={key === 0 ? '비고' : undefined} style={{ marginBottom: 6 }}>
                        <Input placeholder="비고" />
                      </Form.Item>
                    </Col>
                    <Col flex="28px">
                      <Button type="text" danger icon={<MinusCircleOutlined />} onClick={() => remove(name)}
                        style={{ marginTop: key === 0 ? 22 : 0, padding: '0 4px' }} />
                    </Col>
                  </Row>
                ))}
                <Button type="dashed" onClick={() => add({ round_no: fields.length + 1, survey_date: null, note: '' })}
                  icon={<PlusOutlined />} style={{ width: '100%', marginBottom: 4, height: 26, fontSize: 12 }}>약해조사 추가</Button>
              </>
            )}
          </Form.List>

          {/* ── 관리현황 ── */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '4px 10px', margin: '6px 0 8px',
            background: '#f0fdf4', borderLeft: '3px solid #059669', borderRadius: '0 6px 6px 0',
          }}>
            <FileDoneOutlined style={{ color: '#059669', fontSize: 12 }} />
            <span style={{ fontWeight: 600, fontSize: 11, color: '#059669' }}>관리현황</span>
          </div>

          {/* Row: 성적서발급일(5) | TIMS(5) | 검토기록지(14) */}
          <Row gutter={8}>
            <Col span={5}>
              <Form.Item name="report_date" label="성적서발급일" style={{ marginBottom: 8 }}>
                <DatePicker format={DATE_FORMAT} style={{ width: '100%' }} placeholder="날짜 선택" />
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item name="tims_status" label="TIMS입력현황" style={{ marginBottom: 8 }}>
                <DatePicker format={DATE_FORMAT} style={{ width: '100%' }} placeholder="날짜 선택" />
              </Form.Item>
            </Col>
            <Col span={14}>
              <Form.Item name="review_record" label="검토기록지" style={{ marginBottom: 8 }}>
                <Input />
              </Form.Item>
            </Col>
          </Row>

          {/* 성적서사진유무 — 반복등록 스타일 박스 */}
          <Form.Item name="has_result_photo" hidden><Input /></Form.Item>
          <Form.Item noStyle shouldUpdate={(p, c) => p.has_result_photo !== c.has_result_photo}>
            {() => {
              const val: boolean | null | undefined = form.getFieldValue('has_result_photo');
              const isTrue = val === true;
              const isFalse = val === false;
              const boxBg = isTrue ? '#f0fdf4' : isFalse ? '#fef2f2' : '#f8fafc';
              const boxBorder = isTrue ? '#bbf7d0' : isFalse ? '#fecaca' : '#e2e8f0';
              return (
                <Form.Item style={{ marginBottom: 0 }}>
                  <div style={{
                    background: boxBg, border: `1px solid ${boxBorder}`,
                    borderRadius: 8, padding: '7px 12px', display: 'flex',
                    alignItems: 'center', gap: 8, transition: 'all 0.2s',
                  }}>
                    <span style={{ fontSize: 13, color: gray[600], fontWeight: 500, flexShrink: 0 }}>성적서사진유무</span>
                    {(['있음', '없음'] as const).map(opt => {
                      const optVal = opt === '있음';
                      const selected = val === optVal;
                      return (
                        <button
                          key={opt}
                          type="button"
                          onClick={() => form.setFieldValue('has_result_photo', selected ? null : optVal)}
                          style={{
                            padding: '3px 12px', borderRadius: 5, fontSize: 12, cursor: 'pointer',
                            border: `1.5px solid ${selected ? (optVal ? '#22c55e' : '#ef4444') : '#e2e8f0'}`,
                            background: selected ? (optVal ? '#dcfce7' : '#fee2e2') : '#fafafa',
                            color: selected ? (optVal ? '#16a34a' : '#dc2626') : '#64748b',
                            fontWeight: selected ? 700 : 400, transition: 'all 0.12s', lineHeight: '20px',
                          }}
                        >{opt}</button>
                      );
                    })}
                    {(val !== null && val !== undefined) && (
                      <button
                        type="button"
                        onClick={() => form.setFieldValue('has_result_photo', null)}
                        style={{ fontSize: 11, color: gray[400], background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px' }}
                      >✕</button>
                    )}
                  </div>
                </Form.Item>
              );
            }}
          </Form.Item>
        </Form>
      </Modal>
      {/* 처리일정 모달 */}
      <Modal
        title={itemModalSchedule ? `처리일정 — ${itemModalSchedule.test_number} ${itemModalSchedule.title}` : '처리일정'}
        open={treatmentModalVisible}
        onCancel={() => setTreatmentModalVisible(false)}
        onOk={handleTreatmentSave}
        confirmLoading={itemModalSaving}
        width={680}
        okText="저장"
        cancelText="취소"
        maskClosable={false}
      >
        <Form form={treatmentForm} size="small">
          <Form.List name="treatments">
            {(fields, { add, remove }) => (
              <>
                <div style={{ display: 'flex', gap: 8, marginBottom: 4, fontWeight: 600, color: gray[500], fontSize: 12, padding: '0 4px' }}>
                  <span style={{ width: 60, flexShrink: 0 }}>차수</span>
                  <span style={{ width: 160, flexShrink: 0 }}>처리일자</span>
                  <span style={{ flex: 1 }}>비고</span>
                  <span style={{ width: 28, flexShrink: 0 }} />
                </div>
                {fields.map(({ key, name, ...rest }) => (
                  <Row gutter={8} key={key} align="middle" style={{ marginBottom: 4 }}>
                    <Col flex="60px">
                      <Form.Item {...rest} name={[name, 'round_no']} noStyle>
                        <InputNumber min={1} max={7} style={{ width: '100%' }} placeholder="차수" />
                      </Form.Item>
                    </Col>
                    <Col flex="160px">
                      <Form.Item {...rest} name={[name, 'treatment_date']} noStyle rules={[{ required: true, message: '처리일자를 입력하세요' }]}>
                        <DateOrTextPicker width="100%" />
                      </Form.Item>
                    </Col>
                    <Col flex="1">
                      <Form.Item {...rest} name={[name, 'note']} noStyle>
                        <Input placeholder="비고" />
                      </Form.Item>
                    </Col>
                    <Col flex="28px">
                      <Button type="text" danger icon={<MinusCircleOutlined />} onClick={() => remove(name)} />
                    </Col>
                  </Row>
                ))}
                <Button
                  type="dashed"
                  onClick={() => add({ round_no: fields.length + 1, treatment_date: null, note: '' })}
                  icon={<PlusOutlined />}
                  style={{ width: '100%', marginTop: 8, borderColor: '#7c3aed', color: '#7c3aed' }}
                >
                  처리 추가
                </Button>
              </>
            )}
          </Form.List>
        </Form>
      </Modal>

      {/* ── 진행 타임라인 모달 ── */}
      <Modal
        title={timelineRecord ? (
          <span>
            <HistoryOutlined style={{ color: '#8b5cf6', marginRight: 8 }} />
            진행 타임라인 — <span style={{ color: brand.primary, fontWeight: 700 }}>{timelineRecord.test_number}</span>
            <span style={{ fontWeight: 400, color: gray[500], marginLeft: 8, fontSize: 13 }}>{timelineRecord.title}</span>
          </span>
        ) : '진행 타임라인'}
        open={timelineVisible}
        onCancel={() => setTimelineVisible(false)}
        footer={<Button onClick={() => setTimelineVisible(false)}>닫기</Button>}
        width={700}
        maskClosable={false}
      >
        {timelineRecord && (() => {
          interface TimelineEvent {
            date: string; label: string; actor: 'sappt' | 'partner';
            icon: React.ReactNode; note?: string;
            color: string; bg: string; border: string;
          }
          // 유형별 색상 — 색상환 균등 배분으로 최대 대비
          const TYPE_COLOR = {
            start:   { color: '#4338ca', bg: '#eef2ff', border: '#818cf8' }, // 인디고  — 시험개시
            treat:   { color: '#0369a1', bg: '#e0f2fe', border: '#38bdf8' }, // 하늘파랑 — 처리
            yakho:   { color: '#15803d', bg: '#dcfce7', border: '#4ade80' }, // 초록    — 약효조사
            yakhe:   { color: '#c2410c', bg: '#ffedd5', border: '#fb923c' }, // 주황    — 약해조사
            done:    { color: '#0d9488', bg: '#ccfbf1', border: '#2dd4bf' }, // 청록    — 완료/TIMS
            partner: { color: '#b45309', bg: '#fef3c7', border: '#fbbf24' }, // 골드앰버 — 파트너확인
          };
          const events: TimelineEvent[] = [];
          if (timelineRecord.start_date)
            events.push({ date: timelineRecord.start_date, label: '시험 개시', actor: 'sappt', icon: <CalendarOutlined />, ...TYPE_COLOR.start });
          (timelineRecord.treatments || []).filter(t => t.treatment_date).forEach(t =>
            events.push({ date: t.treatment_date, label: `${t.round_no}차 처리`, actor: 'sappt', icon: <ExperimentOutlined />, note: t.note || undefined, ...TYPE_COLOR.treat })
          );
          (timelineRecord.surveys || []).filter(s => s.survey_date).forEach(s => {
            const isYakhe = s.survey_type === '약해';
            events.push({ date: s.survey_date, label: `${s.round_no}차 ${isYakhe ? '약해조사' : '약효조사'}`, actor: 'sappt', icon: <AuditOutlined />, note: s.note || undefined, ...(isYakhe ? TYPE_COLOR.yakhe : TYPE_COLOR.yakho) });
          });
          if (timelineRecord.report_date)
            events.push({ date: timelineRecord.report_date, label: '성적서 발급', actor: 'sappt', icon: <FileDoneOutlined />, ...TYPE_COLOR.done });
          if (timelineRecord.tims_status)
            events.push({ date: timelineRecord.tims_status, label: 'TIMS 입력', actor: 'sappt', icon: <CheckCircleOutlined />, ...TYPE_COLOR.done });
          if (timelineRecord.partner_confirmed_at)
            events.push({ date: timelineRecord.partner_confirmed_at.substring(0, 10), label: '파트너 확인', actor: 'partner', icon: <UserOutlined />, note: timelineRecord.partner_confirmed_by || undefined, ...TYPE_COLOR.partner });

          const isIso = (d: string) => /^\d{4}-\d{2}-\d{2}/.test(d);
          events.sort((a, b) => {
            if (!isIso(a.date) && !isIso(b.date)) return 0;
            if (!isIso(a.date)) return -1;
            if (!isIso(b.date)) return 1;
            return a.date < b.date ? -1 : a.date > b.date ? 1 : 0;
          });

          const sapptCnt = events.filter(e => e.actor === 'sappt').length;
          const partnerCnt = events.filter(e => e.actor === 'partner').length;

          return (
            <div>
              {/* 상단 요약 바 */}
              <div style={{ marginBottom: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8, padding: '8px 14px', background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                  <Tag color={statusTagColors[timelineRecord.status] || '#64748b'} style={{ marginRight: 0, fontWeight: 600 }}>{timelineRecord.status}</Tag>
                  <span style={{ fontSize: 12, color: gray[500] }}><TeamOutlined style={{ marginRight: 4, color: '#4338ca' }} /><span style={{ color: '#4338ca', fontWeight: 600 }}>SAPPT</span> {sapptCnt}건</span>
                  <span style={{ fontSize: 12, color: gray[500] }}><UserOutlined style={{ marginRight: 4, color: '#b45309' }} /><span style={{ color: '#b45309', fontWeight: 600 }}>파트너</span> {partnerCnt}건</span>
                </div>
                {/* 색상 범례 */}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', padding: '6px 10px', background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                  {([
                    { color: '#4338ca', bg: '#eef2ff', label: '시험개시' },
                    { color: '#0369a1', bg: '#e0f2fe', label: '처리' },
                    { color: '#15803d', bg: '#dcfce7', label: '약효조사' },
                    { color: '#c2410c', bg: '#ffedd5', label: '약해조사' },
                    { color: '#0d9488', bg: '#ccfbf1', label: '완료/TIMS' },
                    { color: '#b45309', bg: '#fef3c7', label: '파트너확인' },
                  ] as const).map(l => (
                    <span key={l.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 500, color: l.color, background: l.bg, border: `1px solid ${l.color}44`, borderRadius: 12, padding: '2px 8px' }}>
                      <span style={{ width: 7, height: 7, borderRadius: '50%', background: l.color, display: 'inline-block', flexShrink: 0 }} />{l.label}
                    </span>
                  ))}
                </div>
              </div>

              {events.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 0', color: gray[400] }}>등록된 일정 정보가 없습니다.</div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 86px 1fr', gap: 0 }}>
                  {/* 컬럼 헤더 */}
                  <div style={{ textAlign: 'right', paddingRight: 12, paddingBottom: 8 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#4338ca', letterSpacing: '0.04em' }}><TeamOutlined style={{ marginRight: 4 }} />SAPPT</span>
                  </div>
                  <div />
                  <div style={{ paddingLeft: 12, paddingBottom: 8 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#b45309', letterSpacing: '0.04em' }}><UserOutlined style={{ marginRight: 4 }} />의뢰사</span>
                  </div>

                  {/* 이벤트 행 */}
                  {events.map((ev, idx) => (
                    <React.Fragment key={idx}>
                      {/* SAPPT 측 */}
                      <div style={{ textAlign: 'right', paddingRight: 12, paddingBottom: 14, display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        {ev.actor === 'sappt' && (
                          <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end', background: ev.bg, borderLeft: `3px solid ${ev.color}`, border: `1px solid ${ev.border}`, borderRadius: '7px 7px 7px 4px', padding: '5px 11px', maxWidth: 220 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 5, color: ev.color, fontWeight: 700, fontSize: 13 }}>
                              {ev.icon}<span>{ev.label}</span>
                            </div>
                            {ev.note && <span style={{ fontSize: 11, color: gray[400], marginTop: 2 }}>{ev.note}</span>}
                          </div>
                        )}
                      </div>

                      {/* 중앙 타임라인 */}
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
                        {idx > 0 && <div style={{ width: 2, height: 14, background: '#cbd5e1', marginBottom: 2 }} />}
                        <div style={{ width: 14, height: 14, borderRadius: '50%', background: ev.color, border: '2.5px solid #fff', boxShadow: `0 0 0 2.5px ${ev.color}60`, flexShrink: 0, zIndex: 1 }} />
                        <div style={{ fontSize: 10, color: gray[500], fontWeight: 500, marginTop: 3, textAlign: 'center', whiteSpace: 'nowrap', lineHeight: 1.3 }}>{ev.date}</div>
                        {idx < events.length - 1 && <div style={{ width: 2, flex: 1, minHeight: 10, background: '#cbd5e1', marginTop: 2 }} />}
                      </div>

                      {/* 파트너 측 */}
                      <div style={{ paddingLeft: 12, paddingBottom: 14, display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                        {ev.actor === 'partner' && (
                          <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', background: ev.bg, borderRight: `3px solid ${ev.color}`, border: `1px solid ${ev.border}`, borderRadius: '7px 4px 7px 7px', padding: '5px 11px', maxWidth: 220 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 5, color: ev.color, fontWeight: 700, fontSize: 13 }}>
                              {ev.icon}<span>{ev.label}</span>
                            </div>
                            {ev.note && <span style={{ fontSize: 11, color: gray[400], marginTop: 2 }}>{ev.note}</span>}
                          </div>
                        )}
                      </div>
                    </React.Fragment>
                  ))}
                </div>
              )}

              {/* 미완료 항목 요약 */}
              {(() => {
                const missing: string[] = [];
                if (!timelineRecord.report_date) missing.push('성적서 발급');
                if (!timelineRecord.partner_confirmed_at) missing.push('파트너 확인');
                if (timelineRecord.status !== '최종종료') missing.push(`최종종료 (현재: ${timelineRecord.status})`);
                if (missing.length === 0) return null;
                return (
                  <div style={{ marginTop: 8, padding: '10px 14px', background: '#fefce8', borderRadius: 8, border: '1px solid #fde68a' }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#92400e', marginBottom: 4 }}>⏳ 미완료 항목</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {missing.map(m => <Tag key={m} color="warning" style={{ fontSize: 11 }}>{m}</Tag>)}
                    </div>
                  </div>
                );
              })()}
            </div>
          );
        })()}
      </Modal>

      {/* 조사일정 모달 — type별 단독 표시 */}
      <Modal
        title={
          itemModalSchedule ? (
            <Space size={6}>
              <AuditOutlined style={{ color: surveyModalType === '약효' ? '#0891b2' : '#ea580c' }} />
              <span style={{ color: surveyModalType === '약효' ? '#0891b2' : '#ea580c', fontWeight: 700 }}>
                {surveyModalType === '약효' ? '약효' : '약해'}조사일정
              </span>
              <span style={{ color: gray[500], fontWeight: 400, fontSize: 13 }}>
                — {itemModalSchedule.test_number}
              </span>
            </Space>
          ) : '조사일정'
        }
        open={surveyModalVisible}
        onCancel={() => setSurveyModalVisible(false)}
        onOk={handleSurveySave}
        confirmLoading={itemModalSaving}
        width={640}
        okText="저장"
        cancelText="취소"
        maskClosable={false}
        styles={{ header: {
          borderBottom: `2px solid ${surveyModalType === '약효' ? '#0891b2' : '#ea580c'}`,
          paddingBottom: 12,
        }}}
      >
        <Form form={surveyForm} size="small">
          {surveyModalType === '약효' ? (
            <>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '4px 10px', margin: '8px 0 10px',
                background: '#ecfeff', borderLeft: '3px solid #0891b2', borderRadius: '0 6px 6px 0',
              }}>
                <AuditOutlined style={{ color: '#0891b2', fontSize: 12 }} />
                <span style={{ fontWeight: 600, fontSize: 11, color: '#0891b2' }}>약효조사일정 등록/수정</span>
              </div>
              <Form.List name="surveys_yakho">
                {(fields, { add, remove }) => (
                  <>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 4, fontWeight: 600, color: gray[500], fontSize: 12, padding: '0 4px' }}>
                      <span style={{ width: 60, flexShrink: 0 }}>차수</span>
                      <span style={{ width: 160, flexShrink: 0 }}>약효조사일자</span>
                      <span style={{ flex: 1 }}>비고</span>
                      <span style={{ width: 28 }} />
                    </div>
                    {fields.map(({ key, name, ...rest }) => (
                      <Row gutter={8} key={key} align="middle" style={{ marginBottom: 4 }}>
                        <Col flex="60px"><Form.Item {...rest} name={[name, 'round_no']} noStyle><InputNumber min={1} max={10} style={{ width: '100%' }} /></Form.Item></Col>
                        <Col flex="160px"><Form.Item {...rest} name={[name, 'survey_date']} noStyle rules={[{ required: true, message: '필수' }]}><DateOrTextPicker width="100%" /></Form.Item></Col>
                        <Col flex="1"><Form.Item {...rest} name={[name, 'note']} noStyle><Input placeholder="비고" /></Form.Item></Col>
                        <Col flex="28px"><Button type="text" danger icon={<MinusCircleOutlined />} onClick={() => remove(name)} /></Col>
                      </Row>
                    ))}
                    <Button type="dashed" onClick={() => add({ round_no: fields.length + 1, survey_date: null, note: '' })}
                      icon={<PlusOutlined />} style={{ width: '100%', marginTop: 4, borderColor: '#0891b2', color: '#0891b2' }}>약효조사 추가</Button>
                  </>
                )}
              </Form.List>
            </>
          ) : (
            <>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '4px 10px', margin: '8px 0 10px',
                background: '#fff7ed', borderLeft: '3px solid #ea580c', borderRadius: '0 6px 6px 0',
              }}>
                <AuditOutlined style={{ color: '#ea580c', fontSize: 12 }} />
                <span style={{ fontWeight: 600, fontSize: 11, color: '#ea580c' }}>약해조사일정 등록/수정</span>
              </div>
              <Form.List name="surveys_yakhe">
                {(fields, { add, remove }) => (
                  <>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 4, fontWeight: 600, color: gray[500], fontSize: 12, padding: '0 4px' }}>
                      <span style={{ width: 60, flexShrink: 0 }}>차수</span>
                      <span style={{ width: 160, flexShrink: 0 }}>약해조사일자</span>
                      <span style={{ flex: 1 }}>비고</span>
                      <span style={{ width: 28 }} />
                    </div>
                    {fields.map(({ key, name, ...rest }) => (
                      <Row gutter={8} key={key} align="middle" style={{ marginBottom: 4 }}>
                        <Col flex="60px"><Form.Item {...rest} name={[name, 'round_no']} noStyle><InputNumber min={1} max={10} style={{ width: '100%' }} /></Form.Item></Col>
                        <Col flex="160px"><Form.Item {...rest} name={[name, 'survey_date']} noStyle><DateOrTextPicker width="100%" /></Form.Item></Col>
                        <Col flex="1"><Form.Item {...rest} name={[name, 'note']} noStyle><Input placeholder="비고" /></Form.Item></Col>
                        <Col flex="28px"><Button type="text" danger icon={<MinusCircleOutlined />} onClick={() => remove(name)} /></Col>
                      </Row>
                    ))}
                    <Button type="dashed" onClick={() => add({ round_no: fields.length + 1, survey_date: null, note: '' })}
                      icon={<PlusOutlined />} style={{ width: '100%', marginTop: 4, borderColor: '#ea580c', color: '#ea580c' }}>약해조사 추가</Button>
                  </>
                )}
              </Form.List>
            </>
          )}
        </Form>
      </Modal>

      {/* ── 출장 등록 모달 ── */}
      <Modal
        title={<Space size={6}><CarOutlined style={{ color: brand.primary }} /><span style={{ fontWeight: 700 }}>업무 및 출장 등록</span></Space>}
        open={tripModalVisible}
        onCancel={() => setTripModalVisible(false)}
        onOk={handleTripSave}
        confirmLoading={tripSaving}
        okText="등록"
        cancelText="취소"
        width={580}
        okButtonProps={{ style: { background: brand.primary, borderColor: brand.primary } }}
        styles={{ body: { padding: '16px 20px 4px' } }}
      >
        <Form form={tripForm} layout="vertical" size="middle" initialValues={{ work_type: '외근' }}>

          {/* 구분 */}
          <Form.Item label="구분" name="work_type" style={{ marginBottom: 10 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {['out', 'in', 'leave'].map((group, gi) => (
                <React.Fragment key={group}>
                  {TRIP_WORK_TYPES.filter(t => t.group === group).map(t => {
                    const colors = TRIP_TYPE_COLOR[group];
                    const selected = (tripWatchedWorkType || '외근') === t.value;
                    return (
                      <button key={t.value} type="button"
                        onClick={() => tripForm.setFieldValue('work_type', t.value)}
                        style={{
                          padding: '3px 9px', borderRadius: 5, fontSize: 12, cursor: 'pointer',
                          border: `1.5px solid ${selected ? colors.border : '#e2e8f0'}`,
                          background: selected ? colors.bg : '#fafafa',
                          color: selected ? colors.text : '#64748b',
                          fontWeight: selected ? 700 : 400, lineHeight: '20px',
                        }}>
                        {t.icon} {t.label}
                      </button>
                    );
                  })}
                  {gi < 2 && <div style={{ width: 1, background: '#e2e8f0', margin: '0 3px' }} />}
                </React.Fragment>
              ))}
            </div>
          </Form.Item>

          {/* 일정 + 기간 */}
          <Row gutter={8}>
            <Col span={16}>
              <Form.Item label="일정" name="date_range" rules={[{ required: true, message: '날짜를 선택하세요.' }]} style={{ marginBottom: 10 }}>
                <RangePicker style={{ width: '100%' }} placeholder={['시작일', '종료일']} allowEmpty={[false, true]} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="기간" name="duration" style={{ marginBottom: 10 }}>
                <Select placeholder="기간" allowClear>
                  <Option value="종일">종일</Option>
                  <Option value="오전">오전</Option>
                  <Option value="오후">오후</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {/* 연구원 + 차량 */}
          <Row gutter={8}>
            <Col span={isTripOutdoor(tripWatchedWorkType) ? 14 : 24}>
              <Form.Item label="연구원" name="researchers" rules={[{ required: true, message: '연구원을 입력하세요.' }]} style={{ marginBottom: 10 }}>
                <Select mode="tags" placeholder="연구원 선택 또는 입력"
                  options={tripResearchers.map(r => ({ value: r, label: r }))}
                  tokenSeparators={[',']} />
              </Form.Item>
            </Col>
            {isTripOutdoor(tripWatchedWorkType) && (
              <Col span={10}>
                <Form.Item label="차량" name="vehicle_number" style={{ marginBottom: 10 }}>
                  <Select mode="tags" placeholder="차량 선택"
                    options={tripVehicles.map(v => ({ value: v, label: v }))}
                    tokenSeparators={[',']} />
                </Form.Item>
              </Col>
            )}
          </Row>

          {/* 작업내용 */}
          <Form.Item label="작업내용" name="work_content" style={{ marginBottom: 10 }}>
            <Input.TextArea placeholder="작업 내용 또는 사유" autoSize={{ minRows: 2, maxRows: 4 }} />
          </Form.Item>

          {/* 외근 전용 */}
          {isTripOutdoor(tripWatchedWorkType) && (
            <Row gutter={8}>
              <Col span={14}>
                <Form.Item label="출장지" name="destination" style={{ marginBottom: 10 }}>
                  <Input prefix={<EnvironmentOutlined style={{ color: gray[400] }} />} placeholder="예: 서산, 보령" />
                </Form.Item>
              </Col>
              <Col span={10}>
                <Form.Item label="시험번호" name="test_number" style={{ marginBottom: 10 }}>
                  <Input readOnly style={{ background: '#f8fafc', color: brand.primary, fontWeight: 600 }} />
                </Form.Item>
              </Col>
            </Row>
          )}

          {/* 시험책임자 확인일 + 비고 */}
          <Row gutter={8}>
            <Col span={10}>
              <Form.Item label="시험책임자 확인일" name="supervisor_confirmed_at" style={{ marginBottom: 4 }}>
                <DatePicker style={{ width: '100%' }} placeholder="확인일" />
              </Form.Item>
            </Col>
            <Col span={14}>
              <Form.Item label="비고" name="note" style={{ marginBottom: 4 }}>
                <Input.TextArea placeholder="기타 메모" autoSize={{ minRows: 1, maxRows: 3 }} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* 선택 n건 일자 일괄등록 — 성적서발급일 / TIMS입력현황 */}
      <Modal
        title={
          <Space>
            <FileDoneOutlined style={{ color: brand.primary }} />
            <span>일자 일괄등록</span>
            <Tag color={brand.primary} style={{ fontWeight: 600 }}>{selectedIds.length}건</Tag>
          </Space>
        }
        open={bulkDateOpen}
        onCancel={() => setBulkDateOpen(false)}
        onOk={submitBulkDates}
        okText="반영"
        cancelText="취소"
        confirmLoading={bulkDateSaving}
        width={480}
        destroyOnClose
      >
        <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 14 }}>
          선택한 {selectedIds.length}건에 아래 일자를 한 번에 등록합니다.
          비워 둔 항목은 <b>기존 값을 그대로 유지</b>합니다. 진행상태는 변경되지 않습니다.
        </Typography.Paragraph>
        <Form form={bulkDateForm} layout="vertical">
          <Form.Item name="report_date" label="성적서발급일" style={{ marginBottom: 12 }}>
            <DatePicker format={DATE_FORMAT} style={{ width: '100%' }} placeholder="날짜 선택 (미입력 시 변경 없음)" />
          </Form.Item>
          <Form.Item name="tims_status" label="TIMS입력현황" style={{ marginBottom: 0 }}>
            <DatePicker format={DATE_FORMAT} style={{ width: '100%' }} placeholder="날짜 선택 (미입력 시 변경 없음)" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 파트너 화면 미리보기 — partner.sappt.co.kr 상세 모달과 동일 레이아웃 (읽기 전용) */}
      <PartnerViewModal
        open={partnerViewVisible}
        scheduleId={partnerViewRecord?.id ?? null}
        titleRecord={partnerViewRecord}
        statusColorMap={statusTagColors}
        onClose={() => setPartnerViewVisible(false)}
      />
    </div>
  );
};

export default SchedulePage;
